// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../react-app-env.d.ts" />
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Bot,
  CheckCircle2,
  CreditCard,
  Link2,
  Send,
  Star,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/layout/Layout.tsx';
// @ts-ignore CSS side-effect import; no tsconfig.json in this project for the editor to resolve it
import './FormPrompt.css';
import ProductCheckoutModal from '../../../billing/ProductCheckoutModal';
import { formatMarkdownToSafeHtml } from '../../../../utils/html';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { getIfoodErrorInfo } from '../../../../utils/ifoodError';
import { getIfoodImportStatus } from '../../../../services/api';
import {
  expandWeeklyScheduleOvernights,
} from '../../../common/addressSchedule/scheduleUtils.ts';
import { validateFormPrompt } from '../utils/formPromptValidation.ts';
import {
  fetchPromptConfig,
  geocodePromptAddress,
  savePromptConfig,
  savePromptDraft,
  testPromptConfigChat,
} from '../services/api.ts';
import { useBranding } from '../../../../branding/BrandingContext';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';
import {
  AI_CONFIGURATION_STEPS,
  getAIConfigurationStep,
} from '../configuration/steps.ts';
import type { AIConfigurationStepId } from '../configuration/steps.ts';
import {
  AIConfigurationLayout,
  BottomNavigation,
  ConfigurationHeader,
  ConversationTestDrawer,
} from '../components/configuration/AIConfigurationUI.tsx';
import type {
  ConfigurationNavigationStep,
  ConfigurationStepStatus,
  ModeSelectOption,
} from '../components/configuration/AIConfigurationUI.tsx';
import {
  DraftFieldsContext,
} from '../components/configuration/FormPromptControls.tsx';
import type { ReviewSummaryItem, ReviewSummaryStatus } from '../components/configuration/ReviewSummary.tsx';
import PersonalityStep from '../components/configuration/PersonalityStep.tsx';
import CapabilitiesStep from '../components/configuration/CapabilitiesStep.tsx';
import RestaurantStep from '../components/configuration/RestaurantStep.tsx';
import IfoodPromptImportDialog from '../components/configuration/IfoodPromptImportDialog.tsx';
import OrdersStep from '../components/configuration/OrdersStep.tsx';
import FulfillmentStep from '../components/configuration/FulfillmentStep.tsx';
import AdditionalStep from '../components/configuration/AdditionalStep.tsx';
import ReviewStep from '../components/configuration/ReviewStep.tsx';
import { notifyAtendabotPromptStatus } from '../utils/promptStatus.ts';
import { notifyAtendabotOrderControl } from '../utils/orderControl.ts';
import { buildPersonalityPreview } from '../utils/personalityPreview.ts';
import { shouldShowIfoodImportInvite } from '../utils/ifoodImportVisibility.ts';

type DayPeriod = { start: string; end: string };
type DaySchedule = { day: string; label: string; closed: boolean; periods: DayPeriod[] };
type SpecialDate = { date: string; label: string; closed: boolean; periods: DayPeriod[] };
type GeocodeResult = {
  place_id?: string;
  formatted_address?: string;
  lat?: number | null;
  long?: number | null;
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  postal_code?: string;
};
type FreightRow = {
  km?: string;
  raio?: string;
  distancia?: string;
  area?: string;
  bairro?: string;
  cep?: string;
  bairro_ou_cep?: string;
  preco?: string;
  valor?: string;
  taxa?: string;
  tempo?: string;
  tempo_estimado?: string;
};
type PromptPreviewChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};
type PendingErrorFocus = {
  key: string;
  tabId: AIConfigurationStepId;
};

interface PromptFormState {
  nome_empresa: string;
  forma_de_chamada: string;
  categoria_principal: string;
  telefone_ligacoes: string;
  cidade: string;
  estado: string;
  rua: string;
  numero: string;
  bairro: string;
  cep: string;
  endereco_lat: string;
  endereco_long: string;
  possui_atendimento_presencial: boolean | null;
  cnpj: string;
  email_atendimento: string;
  historia_da_marca: string;
  nome_do_agente: string;
  personalidade_principal: string;
  uso_de_emojis: string;
  caracteristicas: string;
  principios: string;
  vicios_de_linguagem: string;
  tipos_de_atendimento_ativos: string[];
  frases_ou_regras_especiais: string;
  horarios_semana: DaySchedule[];
  datas_especiais: SpecialDate[];
  pedidos_habilitados: boolean | null;
  divulgar_cardapio: boolean;
  usar_cardapio_onemenu: boolean;
  usar_reservo_reservas: boolean;
  usar_feedbackhero_avaliacao_pedidos: boolean;
  usar_cv_vagas: boolean;
  cardapio_cadastrado: boolean | null;
  cardapio_link_disponivel: boolean | null;
  entrega_ativa: boolean | null;
  retirada_ativa: boolean | null;
  tempo_estimado_entrega_min: string;
  tempo_estimado_entrega_max: string;
  tempo_estimado_retirada_min: string;
  tempo_estimado_retirada_max: string;
  modelo_frete: string;
  tabela_frete: FreightRow[];
  informar_taxa_automaticamente: boolean | null;
  pedir_endereco_antes_estimativa: boolean | null;
  oferecer_retirada_fora_area: boolean | null;
  regra_fora_da_area: string;
  metodos_pagamento: string[];
  bandeiras_aceitas: string[];
  pode_2_cartoes: boolean | null;
  pode_pix_cartao: boolean | null;
  exige_pagamento_antecipado: boolean | null;
  pix_chave: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  google_meu_negocio: string;
  onemenu_link_cardapio: string;
  link_cardapio: string;
  link_reserva_externo: string;
  reservo_link_reservas: string;
  link_vagas: string;
  link_feedback: string;
  site: string;
}

const DAYS = [
  { value: 'seg', label: 'Segunda' },
  { value: 'ter', label: 'Terça' },
  { value: 'qua', label: 'Quarta' },
  { value: 'qui', label: 'Quinta' },
  { value: 'sex', label: 'Sexta' },
  { value: 'sab', label: 'Sábado' },
  { value: 'dom', label: 'Domingo' },
];

const IFOOD_DAY_INDEX_BY_PROMPT_DAY: Record<string, number> = {
  dom: 0,
  seg: 1,
  ter: 2,
  qua: 3,
  qui: 4,
  sex: 5,
  sab: 6,
};

const mapIfoodScheduleToPrompt = (scheduleDays: any[] = []): DaySchedule[] => (
  DAYS.map(({ value, label }) => {
    const dayIndex = IFOOD_DAY_INDEX_BY_PROMPT_DAY[value];
    const source = scheduleDays.find((day) => Number(day?.day_index ?? day?.dayIndex) === dayIndex);
    const periods = Array.isArray(source?.periods)
      ? source.periods
        .map((period: any) => ({
          start: String(period?.start || '').slice(0, 5),
          end: String(period?.end || '').slice(0, 5),
        }))
        .filter((period: DayPeriod) => period.start && period.end)
      : [];
    const closed = source ? Boolean(source.closed) || periods.length === 0 : true;
    return { day: value, label, closed, periods: closed ? [] : periods };
  })
);

type ModoPedidos = 'whatsapp' | 'link';
type ModoReservas = 'whatsapp' | 'link';
type ReservaLinkOrigem = 'reservo' | 'externo';

const SIMPLE_CAPABILITY_CARDS: Array<{
  id: 'fulfillment' | 'reviews' | 'jobs';
  title: string;
  description: string;
  dependency: string;
  icon: LucideIcon;
}> = [
  {
    id: 'fulfillment',
    title: 'Entrega e retirada',
    description: 'A IA informa se você entrega no endereço do cliente, calcula a taxa, informa prazos e orienta a retirada no balcão.',
    dependency: 'Libera a etapa: Entrega e retirada',
    icon: Truck,
  },
  {
    id: 'reviews',
    title: 'Pedir avaliações',
    description: 'Depois que um pedido é finalizado, a IA envia uma pesquisa de avaliação por estrelas.',
    dependency: 'Requer: FeedbackHero ativo',
    icon: Star,
  },
  {
    id: 'jobs',
    title: 'Divulgar vagas',
    description: 'Quando alguém pergunta sobre trabalhar aí, a IA apresenta as vagas abertas e envia o link de candidatura.',
    dependency: 'Requer: Currículo Certo ativo',
    icon: Link2,
  },
];

const PEDIDOS_MODE_OPTIONS: ModeSelectOption[] = [
  {
    id: 'whatsapp',
    label: 'Receber pedidos pelo WhatsApp',
    description: 'A IA mostra o cardápio, monta o pedido item por item na conversa, confirma entrega ou retirada, confirma o pagamento e registra o pedido completo.',
    note: 'Exige: cardápio, entrega/retirada e formas de pagamento.',
  },
  {
    id: 'link',
    label: 'Apenas enviar o link do cardápio',
    description: 'A IA envia o link e o cliente finaliza o pedido por lá. A IA não monta nem registra pedidos na conversa.',
    note: 'Exige: apenas a origem do link (OneMenu ou link externo).',
  },
];

const TYPE_KEY_BY_LABEL: Record<string, string> = {
  'pedidos delivery': 'pedidos_delivery',
  'pedidos retirada': 'pedidos_retirada',
  'consumo no local': 'consumo_no_local',
  reservas: 'reservas',
  eventos: 'eventos',
  encomendas: 'encomendas',
  'dúvidas gerais (horários, endereço, pagamento)': 'duvidas_gerais',
  'vagas/fornecedores': 'vagas_fornecedores_feedback',
  'vagas/fornecedores/feedback': 'vagas_fornecedores_feedback',
};

const PAYMENT_OPTIONS = [
  'dinheiro',
  'pix',
  'crédito',
  'débito',
  'vale-refeição',
];

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const CITY_OPTIONS_BY_UF: Record<string, string[]> = {
  AC: ['Rio Branco', 'Cruzeiro do Sul', 'Sena Madureira'],
  AL: ['Maceió', 'Arapiraca', 'Palmeira dos Índios'],
  AP: ['Macapá', 'Santana', 'Laranjal do Jari'],
  AM: ['Manaus', 'Parintins', 'Itacoatiara'],
  BA: ['Salvador', 'Feira de Santana', 'Vitória da Conquista', 'Ilhéus', 'Porto Seguro'],
  CE: ['Fortaleza', 'Caucaia', 'Juazeiro do Norte', 'Sobral'],
  DF: ['Brasília', 'Taguatinga', 'Ceilândia'],
  ES: ['Vitória', 'Vila Velha', 'Serra', 'Cariacica'],
  GO: ['Goiânia', 'Aparecida de Goiânia', 'Anápolis'],
  MA: ['São Luís', 'Imperatriz', 'Caxias'],
  MT: ['Cuiabá', 'Várzea Grande', 'Rondonópolis'],
  MS: ['Campo Grande', 'Dourados', 'Três Lagoas'],
  MG: ['Belo Horizonte', 'Uberlândia', 'Contagem', 'Juiz de Fora', 'Betim'],
  PA: ['Belém', 'Ananindeua', 'Santarém', 'Marabá'],
  PB: ['João Pessoa', 'Campina Grande', 'Patos'],
  PR: ['Curitiba', 'Londrina', 'Maringá', 'Cascavel', 'Ponta Grossa'],
  PE: ['Recife', 'Jaboatão dos Guararapes', 'Olinda', 'Caruaru'],
  PI: ['Teresina', 'Parnaíba', 'Picos'],
  RJ: ['Rio de Janeiro', 'Niterói', 'Duque de Caxias', 'Nova Iguaçu'],
  RN: ['Natal', 'Mossoró', 'Parnamirim'],
  RS: ['Porto Alegre', 'Caxias do Sul', 'Pelotas', 'Canoas', 'Santa Maria'],
  RO: ['Porto Velho', 'Ji-Paraná', 'Ariquemes'],
  RR: ['Boa Vista', 'Rorainópolis', 'Caracaraí'],
  SC: ['Florianópolis', 'Joinville', 'Blumenau', 'Chapecó', 'Itajaí'],
  SP: ['São Paulo', 'Guarulhos', 'Campinas', 'São Bernardo do Campo', 'Santos', 'Ribeirão Preto'],
  SE: ['Aracaju', 'Nossa Senhora do Socorro', 'Lagarto'],
  TO: ['Palmas', 'Araguaína', 'Gurupi'],
};

const createDefaultSchedule = (): DaySchedule[] =>
  DAYS.map((day) => ({
    day: day.value,
    label: day.label,
    closed: false,
    periods: [],
  }));

const DEFAULT_FORM: PromptFormState = {
  nome_empresa: '',
  forma_de_chamada: '',
  categoria_principal: '',
  telefone_ligacoes: '',
  cidade: '',
  estado: '',
  rua: '',
  numero: '',
  bairro: '',
  cep: '',
  endereco_lat: '',
  endereco_long: '',
  possui_atendimento_presencial: null,
  cnpj: '',
  email_atendimento: '',
  historia_da_marca: '',
  nome_do_agente: '',
  personalidade_principal: '',
  uso_de_emojis: '',
  caracteristicas: '',
  principios: '',
  vicios_de_linguagem: '',
  tipos_de_atendimento_ativos: [],
  frases_ou_regras_especiais: '',
  horarios_semana: createDefaultSchedule(),
  datas_especiais: [],
  pedidos_habilitados: null,
  divulgar_cardapio: false,
  usar_cardapio_onemenu: false,
  usar_reservo_reservas: false,
  usar_feedbackhero_avaliacao_pedidos: false,
  usar_cv_vagas: false,
  cardapio_cadastrado: null,
  cardapio_link_disponivel: null,
  entrega_ativa: null,
  retirada_ativa: null,
  tempo_estimado_entrega_min: '',
  tempo_estimado_entrega_max: '',
  tempo_estimado_retirada_min: '',
  tempo_estimado_retirada_max: '',
  modelo_frete: '',
  tabela_frete: [],
  informar_taxa_automaticamente: null,
  pedir_endereco_antes_estimativa: null,
  oferecer_retirada_fora_area: null,
  regra_fora_da_area: '',
  metodos_pagamento: [],
  bandeiras_aceitas: [],
  pode_2_cartoes: null,
  pode_pix_cartao: null,
  exige_pagamento_antecipado: null,
  pix_chave: '',
  instagram: '',
  facebook: '',
  tiktok: '',
  google_meu_negocio: '',
  onemenu_link_cardapio: '',
  link_cardapio: '',
  link_reserva_externo: '',
  reservo_link_reservas: '',
  link_vagas: '',
  link_feedback: '',
  site: '',
};

const toBool = (value: any): boolean | null => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

const normalizeUsarCardapioOneMenu = (value: any): boolean => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return false;
};

const mergeIgnoringNull = (defaults: PromptFormState, source: any): Record<string, any> => {
  const merged: Record<string, any> = { ...defaults };
  if (!source || typeof source !== 'object') return merged;

  Object.entries(source).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });

  return merged;
};

const normalizeSchedule = (value: any): DaySchedule[] => {
  const base = createDefaultSchedule();
  if (!Array.isArray(value) || value.length === 0) return base;

  const mapped = new Map<string, any>();
  value.forEach((item) => {
    const key = item.day || item.dia || item.label;
    if (key) mapped.set(String(key), item);
  });

  return base.map((day) => {
    const incoming = mapped.get(day.day) || mapped.get(day.label) || {};
    const periodsRaw = incoming.periods || incoming.periodos || [];
    const periods = Array.isArray(periodsRaw)
      ? periodsRaw.map((period: any) => ({
          start: String(period.start || period.inicio || '').trim(),
          end: String(period.end || period.fim || '').trim(),
        }))
      : [];

    return {
      ...day,
      closed: Boolean(incoming.closed ?? incoming.fechado ?? false),
      periods,
    };
  });
};

const normalizeSpecialDates = (value: any): SpecialDate[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const periodsRaw = item.periods || item.periodos || [];
    return {
      date: String(item.date || item.data || '').trim(),
      label: String(item.label || item.descricao || item.titulo || '').trim(),
      closed: Boolean(item.closed ?? item.fechado ?? false),
      periods: Array.isArray(periodsRaw)
        ? periodsRaw.map((period: any) => ({
            start: String(period.start || period.inicio || '').trim(),
            end: String(period.end || period.fim || '').trim(),
          }))
        : [],
    };
  });
};

const normalizeEmojiUsage = (value: any): string => {
  const raw = String(value ?? '').trim();
  const key = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (key === 'livre' || key === 'normal' || key === 'sempre usar') {
    return 'Sempre usar';
  }

  if (key === 'raramente' || key === 'usar ocasionalmente') {
    return 'Usar ocasionalmente';
  }

  if (key === 'nunca' || key === 'nunca usar') {
    return 'Nunca usar';
  }

  return raw;
};

const DUVIDAS_TYPE_LABEL = 'dúvidas gerais (horários, endereço, pagamento)';

const normalizeAtendimentoTypes = (value: any): string[] => {
  const list = Array.isArray(value)
    ? value.map((item) => (
        item === 'vagas/fornecedores/feedback' ? 'vagas/fornecedores' : item
      ))
    : [];
  const hasDuvidas = list.some((item) => (TYPE_KEY_BY_LABEL[item] || item) === 'duvidas_gerais');
  return hasDuvidas ? list : [...list, DUVIDAS_TYPE_LABEL];
};

const normalizeForm = (data: any): PromptFormState => {
  const merged = mergeIgnoringNull(DEFAULT_FORM, data);

  return {
    ...DEFAULT_FORM,
    ...merged,
    uso_de_emojis: normalizeEmojiUsage(merged.uso_de_emojis),
    cep: formatCep(merged.cep),
    endereco_lat:
      merged.endereco_lat !== null && merged.endereco_lat !== undefined
        ? String(merged.endereco_lat)
        : merged.lat !== null && merged.lat !== undefined
          ? String(merged.lat)
          : '',
    endereco_long:
      merged.endereco_long !== null && merged.endereco_long !== undefined
        ? String(merged.endereco_long)
        : merged.long !== null && merged.long !== undefined
          ? String(merged.long)
          : '',
    possui_atendimento_presencial: toBool(merged.possui_atendimento_presencial),
    pedidos_habilitados: toBool(merged.pedidos_habilitados),
    divulgar_cardapio: Boolean(toBool(merged.divulgar_cardapio)),
    usar_cardapio_onemenu: normalizeUsarCardapioOneMenu(merged.usar_cardapio_onemenu),
    usar_reservo_reservas: Boolean(toBool(merged.usar_reservo_reservas)),
    usar_feedbackhero_avaliacao_pedidos: Boolean(toBool(merged.usar_feedbackhero_avaliacao_pedidos)),
    usar_cv_vagas: Boolean(toBool(merged.usar_cv_vagas)),
    cardapio_cadastrado: toBool(merged.cardapio_cadastrado),
    cardapio_link_disponivel: toBool(merged.cardapio_link_disponivel),
    entrega_ativa: toBool(merged.entrega_ativa),
    retirada_ativa: toBool(merged.retirada_ativa),
    informar_taxa_automaticamente: toBool(merged.informar_taxa_automaticamente),
    pedir_endereco_antes_estimativa: toBool(merged.pedir_endereco_antes_estimativa),
    oferecer_retirada_fora_area: toBool(merged.oferecer_retirada_fora_area),
    pode_2_cartoes: toBool(merged.pode_2_cartoes),
    pode_pix_cartao: toBool(merged.pode_pix_cartao),
    exige_pagamento_antecipado: toBool(merged.exige_pagamento_antecipado),
    tipos_de_atendimento_ativos: normalizeAtendimentoTypes(merged.tipos_de_atendimento_ativos),
    metodos_pagamento: Array.isArray(merged.metodos_pagamento)
      ? merged.metodos_pagamento
      : [],
    bandeiras_aceitas: Array.isArray(merged.bandeiras_aceitas)
      ? merged.bandeiras_aceitas
      : [],
    modelo_frete: normalizeFreteModel(merged.modelo_frete),
    onemenu_link_cardapio: String(merged.onemenu_link_cardapio ?? ''),
    link_reserva_externo: String(merged.link_reserva_externo ?? ''),
    reservo_link_reservas: String(
      merged.reservo_link_reservas ?? merged.link_reservas ?? '',
    ),
    horarios_semana: normalizeSchedule(merged.horarios_semana),
    datas_especiais: normalizeSpecialDates(merged.datas_especiais),
    tabela_frete: Array.isArray(merged.tabela_frete) ? merged.tabela_frete : [],
  };
};

const trimText = (value: unknown): string => String(value ?? '').trim();

const formatCep = (value: unknown): string => {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const normalizeForMatch = (value: unknown): string =>
  trimText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getApiFieldErrors = (error: any): Record<string, string> => {
  const details = error?.response?.data?.details ?? error?.response?.data?.errors;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};

  return Object.entries(details).reduce<Record<string, string>>((acc, [key, value]) => {
    const message = trimText(value);
    if (key && message) acc[key] = message;
    return acc;
  }, {});
};

const getPromptPreviewErrorMessage = (error: any): string => {
  const status = Number(error?.response?.status);
  const apiMessage = getApiErrorMessage(error, '');
  const normalized = normalizeForMatch(apiMessage);

  if (normalized.includes('gemini') || normalized.includes('api key')) {
    return 'Falta configurar a chave de IA deste restaurante. Entre em contato com os administradores do sistema antes de testar a conversa.';
  }

  if (normalized.includes('restaurante id') || normalized.includes('restaurante_id')) {
    return 'Sua sessão não está vinculada a um restaurante. Revise o cadastro do usuário antes de testar a conversa.';
  }

  if (status === 402) {
    return 'A assinatura está inativa. Ative o produto ou regularize a assinatura para testar a conversa.';
  }

  if (status === 403) {
    return 'Este usuário não tem acesso ao AtendaBot. Libere o produto para o usuário ou use uma conta proprietária.';
  }

  if (status === 404) {
    return 'O serviço de teste da conversa não foi encontrado. Verifique se o backend está atualizado e rodando.';
  }

  return apiMessage || 'Não foi possível testar a conversa agora. Verifique o acesso e as configurações do restaurante.';
};

const extractSlugFromPublicLink = (value: unknown): string => {
  const raw = trimText(value);
  if (!raw) return '';
  const withoutQuery = raw.split('?')[0].split('#')[0].replace(/\/+$/, '');
  const chunks = withoutQuery.split('/').filter(Boolean);
  return chunks.length ? chunks[chunks.length - 1] : '';
};

const resolveBrowserOrigin = (): string => {
  if (typeof window === 'undefined' || !window.location?.origin) return '';
  return String(window.location.origin).replace(/\/+$/, '');
};

const buildPublicPlaceholder = (path: string, fallback: string): string => {
  const safePath = trimText(path);
  if (!safePath) return fallback;
  const origin = resolveBrowserOrigin();
  if (!origin) return fallback;
  return `${origin}${safePath.startsWith('/') ? safePath : `/${safePath}`}`;
};

const RESERVO_WHATSAPP_CONTRACT_URL = trimText(
  process.env.REACT_APP_RESERVO_WHATSAPP_CONTRACT_URL,
);
const FEEDBACKHERO_WHATSAPP_CONTRACT_URL = trimText(
  process.env.REACT_APP_FEEDBACKHERO_WHATSAPP_CONTRACT_URL,
);

const buildEnderecoBusca = (values: {
  rua?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
}): string => (
  [values.rua, values.numero, values.bairro, values.cidade, values.estado, values.cep]
    .map((item) => trimText(item))
    .filter(Boolean)
    .join(', ')
);

const readFreteArea = (row: FreightRow): string =>
  String(row.area ?? row.bairro_ou_cep ?? row.bairro ?? row.cep ?? '');

const readFreteKm = (row: FreightRow): string =>
  String(row.km ?? row.raio ?? row.distancia ?? '');

const readFreteTempo = (row: FreightRow): string =>
  String(row.tempo ?? row.tempo_estimado ?? '');

const readFretePreco = (row: FreightRow): string =>
  String(row.preco ?? row.valor ?? row.taxa ?? '');

const normalizeFreteValue = (value: string): string => trimText(value);
const parseFreteNumber = (value: string): number | null => {
  const normalized = normalizeFreteValue(value).replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const normalizeFreteModel = (value: unknown): string => {
  const model = String(value ?? '').trim().toLowerCase();
  return model === 'bairros_cep' || model === 'fixa' || model === 'raio' ? model : '';
};

const buildFreteRowForModel = (model: string): FreightRow => {
  if (model === 'bairros_cep') return { area: '', tempo: '', preco: '' };
  if (model === 'raio') return { km: '', preco: '' };
  if (model === 'fixa') return { tempo: '', preco: '' };
  return {};
};

const normalizeFreteRowForModel = (row: FreightRow, model: string): FreightRow => {
  const area = normalizeFreteValue(readFreteArea(row));
  const km = normalizeFreteValue(readFreteKm(row));
  const tempo = normalizeFreteValue(readFreteTempo(row));
  const preco = normalizeFreteValue(readFretePreco(row));

  if (model === 'bairros_cep') return { area, tempo, preco };
  if (model === 'raio') return { km, preco };
  if (model === 'fixa') return { tempo, preco };
  return {};
};

const normalizeFreteRowsForPayload = (model: string, rows: FreightRow[]): FreightRow[] => {
  if (!Array.isArray(rows)) return [];
  const normalizedModel = normalizeFreteModel(model);
  if (!normalizedModel) return [];

  return rows
    .map((row) => normalizeFreteRowForModel(row || {}, normalizedModel))
    .filter((row) => {
      if (normalizedModel === 'bairros_cep') {
        return Boolean(
          normalizeFreteValue(readFreteArea(row)) ||
          normalizeFreteValue(readFreteTempo(row)) ||
          normalizeFreteValue(readFretePreco(row))
        );
      }
      if (normalizedModel === 'raio') {
        return Boolean(
          normalizeFreteValue(readFreteKm(row)) ||
          normalizeFreteValue(readFretePreco(row))
        );
      }
      if (normalizedModel === 'fixa') {
        return Boolean(
          normalizeFreteValue(readFreteTempo(row)) ||
          normalizeFreteValue(readFretePreco(row))
        );
      }
      return false;
    });
};

const FRETE_MODEL_META: Record<
  string,
  {
    tooltip: string;
    helper: string;
    firstFieldLabel?: string;
    firstFieldPlaceholder?: string;
    rowTooltip: string;
  }
> = {
  bairros_cep: {
    tooltip: 'Use este modelo quando sua taxa muda por bairro.',
    helper: 'Cada linha deve ter: bairro, tempo estimado e valor.',
    firstFieldLabel: 'Bairro',
    firstFieldPlaceholder: 'Ex.: Centro',
    rowTooltip: 'Cada linha: bairro, tempo estimado (min) e valor da taxa.',
  },
  raio: {
    tooltip: 'Use este modelo para cobrar por faixas de distância (km).',
    helper: 'Cadastre faixas de raio com km e valor. O tempo vem do campo estimado de entrega acima.',
    firstFieldLabel: 'Raio (km)',
    firstFieldPlaceholder: 'Ex.: 5',
    rowTooltip: 'Cada linha: km máximo da faixa e valor da taxa.',
  },
  fixa: {
    tooltip: 'Mesma taxa para toda área atendida.',
    helper: 'Cadastre pelo menos uma linha com tempo e valor.',
    rowTooltip: 'Informe tempo estimado (min) e valor da taxa fixa.',
  },
};

const FormPrompt = () => {
  const navigate = useNavigate();
  const branding = useBranding();
  const isAtendabotBrand = branding?.key === 'atendabot';
  const {
    restaurant,
    hasProductAccess,
    hasTrialRestrictedProductAccess: hasTrialRestrictedAtendabotAccess,
    isOwner,
    isAdmin,
  } = useAuthSession();
  const [form, setForm] = useState<PromptFormState>(DEFAULT_FORM);
  const [baselineForm, setBaselineForm] = useState<PromptFormState>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadRequestKey, setLoadRequestKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const saveRequestInFlightRef = useRef(false);
  const [success, setSuccess] = useState(false);
  const [hasUnsavedPromptChanges, setHasUnsavedPromptChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  const [bandeiraInput, setBandeiraInput] = useState('');
  const [showLinksAdvanced, setShowLinksAdvanced] = useState(false);
  const [enderecoBusca, setEnderecoBusca] = useState('');
  const [enderecoResultados, setEnderecoResultados] = useState<GeocodeResult[]>([]);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const [promptPreviewInput, setPromptPreviewInput] = useState('');
  const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);
  const [promptPreviewError, setPromptPreviewError] = useState<string | null>(null);
  const [promptPreviewMessages, setPromptPreviewMessages] = useState<PromptPreviewChatMessage[]>([]);
  const [conversationDrawerOpen, setConversationDrawerOpen] = useState(false);
  const [draftSaveState, setDraftSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const promptPreviewMessagesRef = useRef<HTMLDivElement | null>(null);
  const promptPreviewInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [checkoutProductKey, setCheckoutProductKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AIConfigurationStepId>('capabilities');
  const [validatedSteps, setValidatedSteps] = useState<Set<AIConfigurationStepId>>(new Set());
  const [horariosOpen, setHorariosOpen] = useState(false);
  const [pendingErrorFocus, setPendingErrorFocus] = useState<PendingErrorFocus | null>(null);
  const [pedidosCardOn, setPedidosCardOn] = useState(false);
  const [reservasCardOn, setReservasCardOn] = useState(false);
  const [reservaLinkOrigem, setReservaLinkOrigem] = useState<ReservaLinkOrigem>('externo');
  const [promptReadyToPublish, setPromptReadyToPublish] = useState(true);
  const tabPanelRef = useRef<HTMLDivElement | null>(null);
  const [ifoodImportOpen, setIfoodImportOpen] = useState(false);
  const [ifoodImportLoading, setIfoodImportLoading] = useState(false);
  const [ifoodMerchant, setIfoodMerchant] = useState<{ id: string; name?: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data: any = await fetchPromptConfig();
        const hasDraft = Boolean(data?.draft_config);
        const publishedForm = normalizeForm(data || {});
        const initialForm = hasDraft ? normalizeForm(data.draft_config) : publishedForm;
        if (!initialForm.nome_empresa.trim() && publishedForm.nome_empresa.trim()) {
          initialForm.nome_empresa = publishedForm.nome_empresa;
        }

        setForm(initialForm);
        setBaselineForm(publishedForm);
        setEnderecoBusca(buildEnderecoBusca(initialForm));
        setPedidosCardOn(initialForm.pedidos_habilitados === true || initialForm.divulgar_cardapio === true);
        const initialTypeKeys = new Set(
          initialForm.tipos_de_atendimento_ativos.map((label) => TYPE_KEY_BY_LABEL[label] || label),
        );
        setReservasCardOn(initialTypeKeys.has('reservas'));
        setReservaLinkOrigem(trimText(initialForm.link_reserva_externo) ? 'externo' : 'reservo');
        setHasUnsavedPromptChanges(hasDraft);
        setDraftSaveState(hasDraft ? 'saved' : 'idle');
        setDraftSavedAt(data?.draft_updated_at ? new Date(data.draft_updated_at) : null);
        const readyToPublish = Boolean(data?.prompt_ready_to_publish);
        setPromptReadyToPublish(readyToPublish);
        notifyAtendabotPromptStatus({
          draftPending: hasDraft,
          promptReadyToPublish: readyToPublish,
        });
      } catch (error) {
        console.error(error);
        setLoadError('Não foi possível carregar os dados atuais do atendimento. Nenhuma alteração foi feita.');
      }
      setLoading(false);
    };
    load();
  }, [loadRequestKey]);

  useEffect(() => {
    if (loading || !hasUnsavedPromptChanges) return;
    setDraftSaveState('saving');
    const timeoutId = window.setTimeout(async () => {
      try {
        const response: any = await savePromptDraft(form);
        setDraftSavedAt(response?.draft_updated_at ? new Date(response.draft_updated_at) : new Date());
        setDraftSaveState('saved');
        notifyAtendabotPromptStatus({ draftPending: true });
      } catch (error) {
        console.error('Erro ao salvar rascunho do atendimento:', error);
        setDraftSaveState('error');
        setSaveError('Não foi possível salvar o rascunho agora.');
      }
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [form, hasUnsavedPromptChanges, loading]);

  useEffect(() => {
    if (!conversationDrawerOpen) return;
    const container = promptPreviewMessagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [conversationDrawerOpen, promptPreviewMessages, promptPreviewLoading]);

  useEffect(() => {
    if (!conversationDrawerOpen || promptPreviewLoading) return;
    promptPreviewInputRef.current?.focus();
  }, [conversationDrawerOpen, promptPreviewLoading]);

  const markDirty = () => {
    if (!hasUnsavedPromptChanges) setHasUnsavedPromptChanges(true);
    setDraftSaveState('saving');
    if (success) setSuccess(false);
    if (saveError) setSaveError(null);
    if (Object.keys(warnings).length > 0) setWarnings({});
  };

  const openExternalLink = (url: string) => {
    const target = trimText(url);
    if (!target || typeof window === 'undefined') return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const openRelatedProduct = (path: string) => {
    if (isAtendabotBrand) return;
    navigate(path);
  };

  const openProductCheckout = (productKey: string) => {
    if (isAtendabotBrand && productKey !== 'atendabot') return;
    setCheckoutProductKey(productKey);
  };

  const draftFieldKeys = useMemo(() => {
    if (!hasUnsavedPromptChanges) return new Set<string>();
    const keys = new Set<string>();
    (Object.keys(form) as Array<keyof PromptFormState>).forEach((key) => {
      if (JSON.stringify(form[key]) !== JSON.stringify(baselineForm[key])) {
        keys.add(key);
      }
    });
    return keys;
  }, [form, baselineForm, hasUnsavedPromptChanges]);

  const selectedTypes = useMemo(
    () => {
      const types = new Set(
        form.tipos_de_atendimento_ativos.map((label) => TYPE_KEY_BY_LABEL[label] || label),
      );
      if (isAtendabotBrand) {
        types.delete('pedidos_delivery');
        types.delete('pedidos_retirada');
        types.delete('reservas');
        types.delete('vagas_fornecedores_feedback');
      }
      return types;
    },
    [form.tipos_de_atendimento_ativos, isAtendabotBrand],
  );

  const hasType = (key: string) => selectedTypes.has(key);

  const hasTypeMembershipChanged = (label: string) => (
    form.tipos_de_atendimento_ativos.includes(label) !== baselineForm.tipos_de_atendimento_ativos.includes(label)
  );

  const modoPedidosDirty = draftFieldKeys.has('pedidos_habilitados') || draftFieldKeys.has('divulgar_cardapio');
  const modoReservasDirty =
    draftFieldKeys.has('usar_reservo_reservas') ||
    draftFieldKeys.has('link_reserva_externo') ||
    hasTypeMembershipChanged('reservas');
  const fulfillmentDirty = draftFieldKeys.has('entrega_ativa') || draftFieldKeys.has('retirada_ativa');
  const reviewsDirty = draftFieldKeys.has('usar_feedbackhero_avaliacao_pedidos');
  const jobsDirty = draftFieldKeys.has('usar_cv_vagas') || hasTypeMembershipChanged('vagas/fornecedores');
  const eventosDirty = hasTypeMembershipChanged('eventos');
  const encomendasDirty = hasTypeMembershipChanged('encomendas');
  const hasReservoProduct = !isAtendabotBrand && hasProductAccess('reservo');
  const hasFeedbackHeroProduct = !isAtendabotBrand && hasProductAccess('feedback_hero');
  const hasCvProduct = !isAtendabotBrand && hasProductAccess('cv');
  const pedidosFeatureOn = !isAtendabotBrand && pedidosCardOn;
  const reservasFeatureOn = !isAtendabotBrand && reservasCardOn;

  const modoPedidos: ModoPedidos | null = !pedidosFeatureOn
    ? null
    : form.pedidos_habilitados === true
      ? 'whatsapp'
      : form.divulgar_cardapio === true
        ? 'link'
        : null;
  const modoReservas: ModoReservas | null = !reservasFeatureOn
    ? null
    : hasType('reservas')
      ? (hasReservoProduct && form.usar_reservo_reservas === true ? 'whatsapp' : 'link')
      : null;
  const reservaOrigemEfetiva: ReservaLinkOrigem = hasReservoProduct ? reservaLinkOrigem : 'externo';

  const showHorarios = [
    'pedidos_delivery',
    'pedidos_retirada',
    'consumo_no_local',
    'reservas',
    'encomendas',
    'duvidas_gerais',
  ].some(hasType);

  const showCidadeEstado = ['pedidos_delivery', 'eventos'].some(hasType);
  const showEndereco = [
    'pedidos_delivery',
    'pedidos_retirada',
    'consumo_no_local',
    'reservas',
    'duvidas_gerais',
    'encomendas',
  ].some(hasType);
  const showPresencial = ['consumo_no_local', 'reservas'].some(hasType);
  const showIfoodImportInvite = shouldShowIfoodImportInvite(form);
  const showCardapio = pedidosFeatureOn && modoPedidos !== null;
  const showFeedbackPedidos = !isAtendabotBrand && form.usar_feedbackhero_avaliacao_pedidos === true;
  const usaCardapioOneMenu = !isAtendabotBrand && form.usar_cardapio_onemenu === true;
  const oneMenuAutoLink = trimText(form.onemenu_link_cardapio);
  const externalCardapioInputValue =
    oneMenuAutoLink
    && trimText(form.link_cardapio) === oneMenuAutoLink
      ? ''
      : form.link_cardapio;
  const usaReservoReservas = !isAtendabotBrand && hasReservoProduct && form.usar_reservo_reservas === true;
  const reservoPublicLink = trimText(form.reservo_link_reservas);
  const reservaExternoLink = trimText(form.link_reserva_externo);
  const hasReservoWhatsappContractLink = !isAtendabotBrand && Boolean(RESERVO_WHATSAPP_CONTRACT_URL);
  const hasFeedbackHeroWhatsappContractLink = !isAtendabotBrand && Boolean(FEEDBACKHERO_WHATSAPP_CONTRACT_URL);
  const sessionRestaurantSlug = trimText(
    restaurant?.checkin_slug
      || restaurant?.slug
      || restaurant?.restaurante_slug
      || restaurant?.public_slug,
  );
  const restauranteSlug = extractSlugFromPublicLink(oneMenuAutoLink) || sessionRestaurantSlug;
  const cvAutoLink = restauranteSlug ? buildPublicPlaceholder(
    `/cv/vagas/${restauranteSlug}`,
    'https://foodstation.app/cv/vagas/seu-slug',
  ) : '';
  const cvLinkExample = buildPublicPlaceholder(
    '/cv/vagas/seu-slug',
    'https://foodstation.app/cv/vagas/seu-slug',
  );
  const cvPublicLink = cvAutoLink || trimText(form.link_vagas);
  const usaCvVagas = !isAtendabotBrand && hasCvProduct && form.usar_cv_vagas === true;
  const showEntrega = form.entrega_ativa === true || (form.entrega_ativa === null && ['pedidos_delivery', 'encomendas'].some(hasType));
  const showRetirada = form.retirada_ativa === true || (form.retirada_ativa === null && ['pedidos_retirada', 'encomendas'].some(hasType));
  const eventosOn = hasType('eventos');
  const encomendasOn = hasType('encomendas');
  const vagasOn = !isAtendabotBrand && (hasType('vagas_fornecedores_feedback') || form.usar_cv_vagas === true);
  const visibleCapabilityCards = isAtendabotBrand
    ? SIMPLE_CAPABILITY_CARDS.filter((item) => item.id === 'fulfillment')
    : SIMPLE_CAPABILITY_CARDS;
  const activeCheckoutProductKey = isAtendabotBrand && checkoutProductKey !== 'atendabot'
    ? null
    : checkoutProductKey;
  const regrasEspeciaisOwner: 'restaurant' | 'orders' | null =
    eventosOn || encomendasOn ? 'restaurant' : modoPedidos === 'whatsapp' ? 'orders' : null;
  const showRegras = regrasEspeciaisOwner === 'orders';
  const showLinks = true;
  const showEnderecoTab = showEndereco || showCidadeEstado || showPresencial;
  const fulfillmentForcado = modoPedidos === 'whatsapp';
  const fulfillmentCardOn = form.entrega_ativa === true || form.retirada_ativa === true;
  const stepVisibility: Record<AIConfigurationStepId, boolean> = useMemo(() => ({
    capabilities: true,
    restaurant: true,
    orders: pedidosFeatureOn,
    fulfillment: fulfillmentForcado || fulfillmentCardOn,
    additional: reservasFeatureOn || showFeedbackPedidos || vagasOn,
    personality: true,
    review: true,
  }), [pedidosFeatureOn, fulfillmentForcado, fulfillmentCardOn, reservasFeatureOn, showFeedbackPedidos, vagasOn]);
  const visibleSteps = AI_CONFIGURATION_STEPS.filter((step) => stepVisibility[step.id]);
  const enderecoObrigatorio =
    showEndereco &&
    (!hasType('encomendas') ||
      ['pedidos_delivery', 'pedidos_retirada', 'consumo_no_local', 'reservas', 'duvidas_gerais'].some(hasType) ||
      form.entrega_ativa === true ||
      form.retirada_ativa === true);
  const entregaObrigatoria = hasType('pedidos_delivery');
  const retiradaObrigatoria = hasType('pedidos_retirada');
  const freteMeta = FRETE_MODEL_META[form.modelo_frete] || null;
  const freteHasAreaField = form.modelo_frete === 'bairros_cep';
  const freteHasKmField = form.modelo_frete === 'raio';
  const freteHasTempoField = form.modelo_frete !== 'raio';
  const freteRowGridClass =
    form.modelo_frete === 'bairros_cep'
      ? 'grid gap-2 md:grid-cols-[1.2fr_1fr_1fr_auto]'
      : form.modelo_frete === 'raio'
        ? 'grid gap-2 md:grid-cols-[1fr_1fr_auto]'
        : form.modelo_frete === 'fixa'
      ? 'grid gap-2 md:grid-cols-[1fr_1fr_auto]'
      : 'grid gap-2 md:grid-cols-[1fr_1fr_auto]';
  const atendabotTrialPromptOnly = useMemo(
    () => hasTrialRestrictedAtendabotAccess('atendabot') && !hasProductAccess('atendabot'),
    [hasProductAccess, hasTrialRestrictedAtendabotAccess]
  );

  const tabFieldMap: Record<AIConfigurationStepId, string[]> = useMemo(
    () => ({
      capabilities: [
        'tipos_de_atendimento_ativos',
        'modo_pedidos',
        'modo_reservas',
      ],
      restaurant: [
        'nome_empresa',
        'forma_de_chamada',
        'historia_da_marca',
        'telefone_ligacoes',
        'email_atendimento',
        'site',
        'rua',
        'numero',
        'bairro',
        'cep',
        'cidade',
        'estado',
        'endereco_lat',
        'endereco_long',
        'possui_atendimento_presencial',
        'horarios_semana',
        'metodos_pagamento',
        'pix_chave',
        ...(regrasEspeciaisOwner === 'restaurant' ? ['frases_ou_regras_especiais'] : []),
      ],
      orders: [
        'cardapio_cadastrado',
        'link_cardapio',
        ...(regrasEspeciaisOwner === 'orders' ? ['frases_ou_regras_especiais'] : []),
      ],
      fulfillment: [
        'entrega_ativa',
        'retirada_ativa',
        'tempo_estimado_entrega_min',
        'tempo_estimado_entrega_max',
        'tempo_estimado_retirada_min',
        'tempo_estimado_retirada_max',
        'modelo_frete',
        'tabela_frete',
      ],
      additional: [
        'usar_reservo_reservas',
        'link_reserva_externo',
        'usar_feedbackhero_avaliacao_pedidos',
        'usar_cv_vagas',
        'link_vagas',
      ],
      personality: [
        'nome_do_agente',
        'personalidade_principal',
        'uso_de_emojis',
        'caracteristicas',
        'principios',
        'vicios_de_linguagem',
      ],
      review: [],
    }),
    [regrasEspeciaisOwner],
  );

  const tabHasErrors = useMemo(() => {
    const errorKeys = new Set(Object.keys(errors));
    return AI_CONFIGURATION_STEPS.reduce<Record<AIConfigurationStepId, boolean>>((acc, step) => {
      acc[step.id] = tabFieldMap[step.id].some((fieldKey) => errorKeys.has(fieldKey));
      return acc;
    }, {} as Record<AIConfigurationStepId, boolean>);
  }, [errors, tabFieldMap]);

  const hasMenuConfigured =
    form.usar_cardapio_onemenu === true ||
    form.cardapio_cadastrado === true ||
    Boolean(form.link_cardapio?.trim());
  const hasPagamentoConfigured =
    form.metodos_pagamento.length > 0 &&
    (!form.metodos_pagamento.includes('pix') || Boolean(form.pix_chave?.trim()));
  const pedidoChecklist: Array<{ label: string; ok: boolean; step: AIConfigurationStepId }> = [
    { label: 'Cardápio conectado', ok: hasMenuConfigured, step: 'orders' },
    { label: 'Entrega ou retirada ativa', ok: fulfillmentCardOn, step: 'fulfillment' },
    { label: 'Formas de pagamento definidas', ok: hasPagamentoConfigured, step: 'restaurant' },
  ];
  const hasAnyOptionalCapability =
    pedidosFeatureOn || reservasFeatureOn || fulfillmentCardOn || showFeedbackPedidos || vagasOn;

  const tabCompletion: Record<AIConfigurationStepId, boolean> = {
    capabilities:
      hasAnyOptionalCapability &&
      (!pedidosFeatureOn || modoPedidos !== null) &&
      (!reservasFeatureOn || modoReservas !== null),
    restaurant: Boolean(form.nome_empresa?.trim()),
    orders: modoPedidos !== null && hasMenuConfigured,
    fulfillment: form.entrega_ativa !== null || form.retirada_ativa !== null,
    additional:
      (showFeedbackPedidos || vagasOn || reservasFeatureOn) &&
      (!reservasFeatureOn || modoReservas !== null),
    personality: Boolean(
      form.nome_do_agente?.trim() ||
      form.personalidade_principal?.trim() ||
      form.uso_de_emojis?.trim(),
    ),
    review: promptReadyToPublish && !hasUnsavedPromptChanges,
  };

  useEffect(() => {
    if (stepVisibility[activeTab]) return;
    setActiveTab('capabilities');
  }, [stepVisibility, activeTab]);

  const errorFocusAliasMap: Record<string, string[]> = useMemo(
    () => ({
      tempo_estimado_entrega_max: ['tempo_estimado_entrega_min'],
      tempo_estimado_retirada_max: ['tempo_estimado_retirada_min'],
    }),
    [],
  );

  const findTabByFieldKey = (fieldKey: string): AIConfigurationStepId => {
    const tabMatch = (Object.keys(tabFieldMap) as AIConfigurationStepId[]).find((tabId) =>
      tabFieldMap[tabId].includes(fieldKey),
    );
    return tabMatch || 'capabilities';
  };

  const navigateToFirstError = (validationErrors: Record<string, string>) => {
    const firstErrorKey = Object.keys(validationErrors)[0];
    if (!firstErrorKey) return;
    const targetTab = findTabByFieldKey(firstErrorKey);
    setPendingErrorFocus({ key: firstErrorKey, tabId: targetTab });
    if (activeTab !== targetTab) {
      setActiveTab(targetTab);
    }
  };

  useEffect(() => {
    if (!pendingErrorFocus) return;
    if (activeTab !== pendingErrorFocus.tabId) return;

    const rafId = window.requestAnimationFrame(() => {
      const keyCandidates = [
        pendingErrorFocus.key,
        ...(errorFocusAliasMap[pendingErrorFocus.key] || []),
      ];

      let anchorEl: HTMLElement | null = null;

      for (const key of keyCandidates) {
        anchorEl =
          tabPanelRef.current?.querySelector<HTMLElement>(`[data-error-key="${key}"]`) ||
          document.querySelector<HTMLElement>(`[data-error-key="${key}"]`);
        if (anchorEl) break;
      }

      if (!anchorEl) {
        const fallbackKey = tabFieldMap[pendingErrorFocus.tabId].find((fieldKey) =>
          Boolean(errors[fieldKey]),
        );
        if (fallbackKey) {
          anchorEl =
            tabPanelRef.current?.querySelector<HTMLElement>(`[data-error-key="${fallbackKey}"]`) ||
            document.querySelector<HTMLElement>(`[data-error-key="${fallbackKey}"]`);
        }
      }

      if (!anchorEl) {
        setPendingErrorFocus(null);
        return;
      }

      anchorEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

      const primaryControlSelector =
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';
      const focusableEl = anchorEl.matches(primaryControlSelector)
        ? anchorEl
        : anchorEl.querySelector<HTMLElement>(primaryControlSelector)
          || anchorEl.querySelector<HTMLElement>('[tabindex]:not([tabindex="-1"])');

      if (focusableEl) {
        focusableEl.focus({ preventScroll: true });
      } else {
        anchorEl.setAttribute('tabindex', '-1');
        anchorEl.focus({ preventScroll: true });
      }

      setPendingErrorFocus(null);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [activeTab, errorFocusAliasMap, errors, pendingErrorFocus, tabFieldMap]);

  const clearFieldErrors = (...keys: string[]) => {
    setErrors((prev) => {
      if (!keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const setModoPedidos = (mode: ModoPedidos) => {
    if (isAtendabotBrand) return;
    markDirty();
    clearFieldErrors('modo_pedidos');
    setPedidosCardOn(true);
    setForm((prev) => ({
      ...prev,
      pedidos_habilitados: mode === 'whatsapp',
      divulgar_cardapio: mode === 'link',
    }));
  };

  const togglePedidosCard = () => {
    if (isAtendabotBrand) return;
    markDirty();
    clearFieldErrors('modo_pedidos');
    if (pedidosCardOn) {
      setPedidosCardOn(false);
      setForm((prev) => ({ ...prev, pedidos_habilitados: false, divulgar_cardapio: false }));
      return;
    }
    setPedidosCardOn(true);
    setForm((prev) => ({
      ...prev,
      entrega_ativa: prev.entrega_ativa === null ? true : prev.entrega_ativa,
      retirada_ativa: prev.retirada_ativa === null ? true : prev.retirada_ativa,
    }));
  };

  const setModoReservas = (mode: ModoReservas) => {
    if (isAtendabotBrand) return;
    if (mode === 'whatsapp' && !hasReservoProduct) return;
    markDirty();
    clearFieldErrors('modo_reservas');
    setReservasCardOn(true);
    setForm((prev) => {
      const types = new Set(prev.tipos_de_atendimento_ativos);
      types.add('reservas');
      return {
        ...prev,
        usar_reservo_reservas: mode === 'whatsapp',
        tipos_de_atendimento_ativos: Array.from(types),
      };
    });
  };

  const toggleReservasCard = () => {
    if (isAtendabotBrand) return;
    markDirty();
    clearFieldErrors('modo_reservas');
    if (reservasCardOn) {
      setReservasCardOn(false);
      setForm((prev) => {
        const types = new Set(prev.tipos_de_atendimento_ativos);
        types.delete('reservas');
        return {
          ...prev,
          usar_reservo_reservas: false,
          tipos_de_atendimento_ativos: Array.from(types),
        };
      });
      return;
    }
    setReservasCardOn(true);
  };

  const toggleCapability = (id: 'fulfillment' | 'reviews' | 'jobs') => {
    if (isAtendabotBrand && id !== 'fulfillment') return;
    markDirty();
    setForm((prev) => {
      const types = new Set(prev.tipos_de_atendimento_ativos);
      const setType = (label: string, enabled: boolean) => {
        if (enabled) types.add(label);
        else types.delete(label);
      };

      if (id === 'fulfillment') {
        const enabled = !(prev.entrega_ativa === true || prev.retirada_ativa === true);
        setType('pedidos delivery', enabled);
        setType('pedidos retirada', enabled || prev.pedidos_habilitados === true);
        return {
          ...prev,
          entrega_ativa: enabled,
          retirada_ativa: enabled,
          tipos_de_atendimento_ativos: Array.from(types),
        };
      }

      if (id === 'reviews') {
        return { ...prev, usar_feedbackhero_avaliacao_pedidos: !prev.usar_feedbackhero_avaliacao_pedidos };
      }

      const enabled = !types.has('vagas/fornecedores');
      setType('vagas/fornecedores', enabled);
      return {
        ...prev,
        usar_cv_vagas: enabled,
        tipos_de_atendimento_ativos: Array.from(types),
      };
    });
  };

  const activeCapabilityTitles = [
    'Responder dúvidas',
    modoPedidos === 'whatsapp'
      ? 'Registrar pedidos pelo WhatsApp'
      : modoPedidos === 'link'
        ? 'Enviar o link do cardápio'
        : '',
    fulfillmentForcado || fulfillmentCardOn ? 'Informar entrega e retirada' : '',
    modoReservas === 'whatsapp'
      ? 'Registrar reservas na conversa (Reservo)'
      : modoReservas === 'link'
        ? 'Enviar o link de reservas'
        : '',
    showFeedbackPedidos ? 'Pedir avaliações' : '',
    vagasOn ? 'Divulgar vagas' : '',
    eventosOn ? 'Orientar eventos' : '',
    encomendasOn ? 'Orientar encomendas' : '',
  ].filter(Boolean);

  const reservasModeOptions: ModeSelectOption[] = [
    {
      id: 'whatsapp',
      label: 'Fazer a reserva pelo WhatsApp',
      description: 'A IA consulta os horários disponíveis, coleta os dados do cliente e registra a reserva na conversa.',
      note: hasReservoProduct ? 'Usa o Reservo conectado.' : undefined,
      disabled: !hasReservoProduct,
      disabledNote: !hasReservoProduct ? 'Requer o Reservo ativo.' : undefined,
      action: hasReservoProduct
        ? undefined
        : { label: 'Ativar Reservo', onClick: () => openProductCheckout('reservo') },
    },
    {
      id: 'link',
      label: 'Apenas enviar o link de reservas',
      description: hasReservoProduct
        ? 'A IA envia o link e o cliente reserva por lá. Pode ser o link automático do Reservo ou o de outra plataforma.'
        : 'A IA envia o link da sua plataforma de reservas e o cliente reserva por lá.',
    },
  ];

  const reservaOrigemOptions: ModeSelectOption[] = [
    {
      id: 'reservo',
      label: 'Link do Reservo (automático)',
      description: reservoPublicLink
        ? `A IA envia: ${reservoPublicLink}`
        : 'O link público é gerado automaticamente quando o restaurante estiver publicado no Reservo. Você não digita nada.',
    },
    {
      id: 'externo',
      label: 'Link de outra plataforma',
      description: 'Informe abaixo o link que a IA deve enviar.',
    },
  ];

  const cardapioLinkPreview = trimText(form.link_cardapio) || oneMenuAutoLink || '{link do cardápio}';
  const reservaLinkPreview = reservaOrigemEfetiva === 'reservo'
    ? (reservoPublicLink || '{link do Reservo}')
    : (trimText(form.link_reserva_externo) || '{link de reservas}');

  const reviewPublished = tabCompletion.review;

  const navigationSteps: ConfigurationNavigationStep[] = visibleSteps.map((step) => {
    if (step.id === activeTab) return { ...step, status: 'current' as ConfigurationStepStatus };
    if (step.id === 'review') {
      return { ...step, status: (reviewPublished ? 'published' : 'needs-publish') as ConfigurationStepStatus };
    }
    let status: ConfigurationStepStatus = 'pending';
    if (tabHasErrors[step.id]) status = 'attention';
    else if (validatedSteps.has(step.id) || tabCompletion[step.id]) status = 'completed';
    return { ...step, status };
  });

  const activeStepIndex = visibleSteps.findIndex((step) => step.id === activeTab);
  const activeStepDefinition = getAIConfigurationStep(activeTab);
  const ActiveStepIcon = activeStepDefinition.icon;
  const mapPreviewUrl = useMemo(() => {
    const lat = Number(form.endereco_lat);
    const long = Number(form.endereco_long);
    if (!Number.isFinite(lat) || !Number.isFinite(long) || !form.endereco_lat || !form.endereco_long) return '';
    const delta = 0.008;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${long - delta}%2C${lat - delta}%2C${long + delta}%2C${lat + delta}&layer=mapnik&marker=${lat}%2C${long}`;
  }, [form.endereco_lat, form.endereco_long]);
  const openingHoursSummary = useMemo(() => {
    const weekdays = form.horarios_semana.slice(0, 5);
    const scheduleText = (day: DaySchedule) => day.closed
      ? 'Fechado'
      : day.periods.map((period) => `${period.start || '--:--'} às ${period.end || '--:--'}`).join(' e ');
    const firstWeekday = weekdays[0];
    if (firstWeekday && weekdays.every((day) => scheduleText(day) === scheduleText(firstWeekday))) {
      return `Segunda a sexta: ${scheduleText(firstWeekday)}`;
    }
    const openDays = form.horarios_semana.filter((day) => !day.closed && day.periods.length > 0);
    return openDays.length > 0 ? `${openDays.length} dias com horários configurados` : 'Nenhum horário configurado';
  }, [form.horarios_semana]);
  const deliveryPreviewText = useMemo(() => {
    const prices = form.tabela_frete
      .map((row) => parseFreteNumber(readFretePreco(row)))
      .filter((value): value is number => value !== null);
    const prazoTexto = `o prazo estimado é de ${form.tempo_estimado_entrega_min || 'não informado'} a ${form.tempo_estimado_entrega_max || 'não informado'} minutos`;
    if (prices.length === 0) {
      return `Cadastre as regras de taxa para a IA informar o valor da entrega. Por enquanto, ${prazoTexto}.`;
    }
    const toCurrency = (value: number) =>
      value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    if (minPrice === maxPrice) {
      return `A taxa de entrega é ${toCurrency(minPrice)} e ${prazoTexto}.`;
    }
    return `A taxa de entrega vai de ${toCurrency(minPrice)} a ${toCurrency(maxPrice)}, conforme o endereço do cliente, e ${prazoTexto}.`;
  }, [form.tabela_frete, form.tempo_estimado_entrega_min, form.tempo_estimado_entrega_max]);
  const personalityPreview = useMemo(
    () => buildPersonalityPreview({
      nome_do_agente: form.nome_do_agente,
      nome_empresa: form.nome_empresa,
      personalidade_principal: form.personalidade_principal,
      uso_de_emojis: form.uso_de_emojis,
    }),
    [form.nome_do_agente, form.nome_empresa, form.personalidade_principal, form.uso_de_emojis],
  );

  const updateField = <K extends keyof PromptFormState>(
    key: K,
    value: PromptFormState[K],
  ) => {
    markDirty();
    if (key === 'tempo_estimado_entrega_min' || key === 'tempo_estimado_entrega_max') {
      clearFieldErrors('tempo_estimado_entrega_min', 'tempo_estimado_entrega_max');
    } else if (key === 'tempo_estimado_retirada_min' || key === 'tempo_estimado_retirada_max') {
      clearFieldErrors('tempo_estimado_retirada_min', 'tempo_estimado_retirada_max');
    } else {
      clearFieldErrors(String(key));
    }
    const normalizedValue = key === 'cep' ? formatCep(value) as PromptFormState[K] : value;
    setForm((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const updateFulfillmentAvailability = (
    field: 'entrega_ativa' | 'retirada_ativa',
    typeLabel: 'pedidos delivery' | 'pedidos retirada',
    value: boolean | null,
  ) => {
    markDirty();
    setForm((prev) => {
      const types = new Set(prev.tipos_de_atendimento_ativos);
      if (value === true) types.add(typeLabel);
      else types.delete(typeLabel);
      return {
        ...prev,
        [field]: value,
        tipos_de_atendimento_ativos: Array.from(types),
      };
    });
  };

  const updateModeloFrete = (model: string) => {
    markDirty();
    const normalizedModel = normalizeFreteModel(model);
    setForm((prev) => ({
      ...prev,
      modelo_frete: normalizedModel,
      tabela_frete: prev.tabela_frete.map((row) => normalizeFreteRowForModel(row || {}, normalizedModel)),
    }));
  };

  const applyEnderecoResultado = (
    resultado: GeocodeResult,
    options?: { keepResults?: boolean },
  ) => {
    if (!resultado) return;

    const keepResults = options?.keepResults === true;
    markDirty();
    setForm((prev) => {
      return {
        ...prev,
        rua: trimText(resultado.street) || prev.rua,
        numero: trimText(resultado.number) || prev.numero,
        bairro: trimText(resultado.neighborhood) || prev.bairro,
        cidade: trimText(resultado.city) || prev.cidade,
        estado: trimText(resultado.state).toUpperCase() || prev.estado,
        cep: formatCep(resultado.postal_code) || prev.cep,
        endereco_lat:
          resultado.lat !== null && resultado.lat !== undefined
            ? String(resultado.lat)
            : prev.endereco_lat,
        endereco_long:
          resultado.long !== null && resultado.long !== undefined
            ? String(resultado.long)
            : prev.endereco_long,
      };
    });

    setEnderecoBusca(resultado.formatted_address || enderecoBusca);
    if (!keepResults) {
      setEnderecoResultados([]);
    }
  };

  const handleBuscarEndereco = async () => {
    const query = trimText(enderecoBusca) || buildEnderecoBusca(form);
    if (!query) {
      setSaveError('Informe rua/bairro/cidade/UF/CEP para buscar o endereço no mapa.');
      return;
    }

    setBuscandoEndereco(true);
    setEnderecoResultados([]);
    try {
      const data = await geocodePromptAddress(query);
      const resultados = Array.isArray(data) ? data : [];
      setEnderecoResultados(resultados);
      if (resultados.length === 0) {
        setSaveError('Nenhum endereço encontrado para a busca informada.');
      } else {
        applyEnderecoResultado(resultados[0], { keepResults: resultados.length > 1 });
        setSaveError(null);
      }
    } catch (error) {
      console.error('Erro ao geocodificar endereço:', error);
      setSaveError('Não foi possível buscar o endereço agora.');
    } finally {
      setBuscandoEndereco(false);
    }
  };

  const handleSelecionarEndereco = (resultado: GeocodeResult) => {
    if (!resultado) return;
    applyEnderecoResultado(resultado);
  };

  const handleOpenIfoodImport = async () => {
    try {
      setIfoodImportLoading(true);
      const integration = await getIfoodImportStatus();
      if (!integration?.connected || !integration?.merchant?.id) {
        navigate('/configuracoes/ifood');
        return;
      }
      setIfoodMerchant({
        id: String(integration.merchant.id),
        name: integration.merchant.name || integration.merchant.corporateName || 'Loja iFood',
      });
      setIfoodImportOpen(true);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Não foi possível consultar a conexão com o iFood.');
      if (getIfoodErrorInfo(error).requiresReauthorization) {
        toast.error(({ closeToast }) => (
          <div>
            <p>{message}</p>
            <button
              type="button"
              onClick={() => {
                closeToast?.();
                navigate('/configuracoes/ifood');
              }}
              className="mt-3 rounded-lg bg-[#EA1D2C] px-3 py-2 text-sm font-bold text-white"
            >
              Refazer conexão
            </button>
          </div>
        ), { autoClose: false });
      } else {
        toast.error(message);
      }
    } finally {
      setIfoodImportLoading(false);
    }
  };

  const handleApplyIfoodImport = ({
    address,
    scheduleDays,
  }: {
    address?: any;
    scheduleDays?: any[];
  }) => {
    markDirty();
    setForm((current) => ({
      ...current,
      ...(address ? {
        rua: trimText(address.street) || current.rua,
        numero: trimText(address.number) || current.numero,
        bairro: trimText(address.district) || current.bairro,
        cidade: trimText(address.city) || current.cidade,
        estado: trimText(address.state).toUpperCase() || current.estado,
        cep: formatCep(address.postal_code) || current.cep,
        endereco_lat: address.latitude !== null && address.latitude !== undefined
          ? String(address.latitude)
          : current.endereco_lat,
        endereco_long: address.longitude !== null && address.longitude !== undefined
          ? String(address.longitude)
          : current.endereco_long,
      } : {}),
      ...(Array.isArray(scheduleDays) ? {
        horarios_semana: mapIfoodScheduleToPrompt(scheduleDays),
      } : {}),
    }));

    if (address) {
      setEnderecoBusca([
        [address.street, address.number].filter(Boolean).join(', '),
        address.district,
        [address.city, address.state].filter(Boolean).join(' - '),
        address.postal_code,
      ].filter(Boolean).join(' · '));
      clearFieldErrors('rua', 'numero', 'bairro', 'cidade', 'estado', 'cep');
    }
    if (Array.isArray(scheduleDays)) {
      setHorariosOpen(true);
      clearFieldErrors('horarios_semana');
    }
    toast.success('Os dados selecionados foram copiados para o AtendaBot. Revise antes de publicar.');
  };

  const toggleArrayValue = (key: 'tipos_de_atendimento_ativos' | 'metodos_pagamento', value: string) => {
    markDirty();
    setForm((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [key]: Array.from(next) };
    });
  };

  const setAtendimentoType = (type: 'eventos' | 'encomendas', enabled: boolean) => {
    markDirty();
    setForm((prev) => {
      const types = new Set(prev.tipos_de_atendimento_ativos);
      if (enabled) types.add(type);
      else types.delete(type);
      return { ...prev, tipos_de_atendimento_ativos: Array.from(types) };
    });
  };

  const addBandeira = () => {
    const value = bandeiraInput.trim();
    if (!value) return;
    markDirty();
    setForm((prev) => ({
      ...prev,
      bandeiras_aceitas: prev.bandeiras_aceitas.includes(value)
        ? prev.bandeiras_aceitas
        : [...prev.bandeiras_aceitas, value],
    }));
    setBandeiraInput('');
  };

  const removeBandeira = (value: string) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      bandeiras_aceitas: prev.bandeiras_aceitas.filter((item) => item !== value),
    }));
  };

  const addPeriod = (index: number) => {
    markDirty();
    setForm((prev) => {
      const schedule = [...prev.horarios_semana];
      schedule[index] = {
        ...schedule[index],
        periods: [...schedule[index].periods, { start: '', end: '' }],
      };
      return { ...prev, horarios_semana: schedule };
    });
  };

  const updatePeriod = (dayIndex: number, periodIndex: number, field: 'start' | 'end', value: string) => {
    markDirty();
    setForm((prev) => {
      const schedule = [...prev.horarios_semana];
      const periods = [...schedule[dayIndex].periods];
      periods[periodIndex] = { ...periods[periodIndex], [field]: value };
      schedule[dayIndex] = { ...schedule[dayIndex], periods };
      return { ...prev, horarios_semana: schedule };
    });
  };

  const removePeriod = (dayIndex: number, periodIndex: number) => {
    markDirty();
    setForm((prev) => {
      const schedule = [...prev.horarios_semana];
      schedule[dayIndex] = {
        ...schedule[dayIndex],
        periods: schedule[dayIndex].periods.filter((_, idx) => idx !== periodIndex),
      };
      return { ...prev, horarios_semana: schedule };
    });
  };

  const toggleDayClosed = (index: number, closed: boolean) => {
    markDirty();
    setForm((prev) => {
      const schedule = [...prev.horarios_semana];
      const currentPeriods = Array.isArray(schedule[index].periods) ? schedule[index].periods : [];
      schedule[index] = {
        ...schedule[index],
        closed,
        periods: closed ? [] : (currentPeriods.length > 0 ? currentPeriods : [{ start: '', end: '' }]),
      };
      return { ...prev, horarios_semana: schedule };
    });
  };

  const addSpecialDate = () => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      datas_especiais: [
        ...prev.datas_especiais,
        { date: '', label: '', closed: false, periods: [] },
      ],
    }));
  };

  const updateSpecialDate = (index: number, field: keyof SpecialDate, value: any) => {
    markDirty();
    setForm((prev) => {
      const dates = [...prev.datas_especiais];
      dates[index] = { ...dates[index], [field]: value };
      return { ...prev, datas_especiais: dates };
    });
  };

  const addSpecialPeriod = (index: number) => {
    markDirty();
    setForm((prev) => {
      const dates = [...prev.datas_especiais];
      const periods = [...dates[index].periods, { start: '', end: '' }];
      dates[index] = { ...dates[index], periods };
      return { ...prev, datas_especiais: dates };
    });
  };

  const updateSpecialPeriod = (index: number, periodIndex: number, field: 'start' | 'end', value: string) => {
    markDirty();
    setForm((prev) => {
      const dates = [...prev.datas_especiais];
      const periods = [...dates[index].periods];
      periods[periodIndex] = { ...periods[periodIndex], [field]: value };
      dates[index] = { ...dates[index], periods };
      return { ...prev, datas_especiais: dates };
    });
  };

  const removeSpecialPeriod = (index: number, periodIndex: number) => {
    markDirty();
    setForm((prev) => {
      const dates = [...prev.datas_especiais];
      dates[index] = {
        ...dates[index],
        periods: dates[index].periods.filter((_, idx) => idx !== periodIndex),
      };
      return { ...prev, datas_especiais: dates };
    });
  };

  const removeSpecialDate = (index: number) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      datas_especiais: prev.datas_especiais.filter((_, idx) => idx !== index),
    }));
  };

  const addFreteRow = () => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      tabela_frete: [...prev.tabela_frete, buildFreteRowForModel(prev.modelo_frete)],
    }));
  };

  const updateFreteRow = (index: number, field: keyof FreightRow, value: string) => {
    markDirty();
    setForm((prev) => {
      const rows = [...prev.tabela_frete];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, tabela_frete: rows };
    });
  };

  const removeFreteRow = (index: number) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      tabela_frete: prev.tabela_frete.filter((_, idx) => idx !== index),
    }));
  };

  const validate = () => validateFormPrompt({
    form,
    hasType,
    pedidosFeatureOn,
    modoPedidos,
    reservasFeatureOn,
    modoReservas,
    showHorarios,
    showEndereco,
    showCidadeEstado,
    reservaOrigemEfetiva,
    usaCvVagas,
    cvPublicLink,
    isAtendabotBrand,
    hasFeedbackHeroProduct,
  });

  const toNumberOrNull = (value: string) => {
    if (!value) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const buildPayload = () => {
    const usandoOneMenu = form.usar_cardapio_onemenu === true;
    const oneMenuLink = trimText(form.onemenu_link_cardapio);
    const linkExternoRaw = trimText(form.link_cardapio);
    const linkExterno = oneMenuLink && linkExternoRaw === oneMenuLink
      ? ''
      : linkExternoRaw;
    const linkCardapioResolvido = usandoOneMenu ? oneMenuLink : linkExterno;
    const linkReservaExterno = modoReservas === 'link' && reservaOrigemEfetiva === 'reservo'
      ? ''
      : trimText(form.link_reserva_externo);
    const usarReservoReservas = modoReservas === 'whatsapp' && hasReservoProduct && form.usar_reservo_reservas === true;
    const usarFeedbackHeroAvaliacaoPedidos =
      hasFeedbackHeroProduct && form.usar_feedbackhero_avaliacao_pedidos === true;
    const usarCvVagas = hasCvProduct && form.usar_cv_vagas === true;
    const linkVagasResolvido = usarCvVagas ? cvPublicLink : '';

    return {
      ...form,
      pedidos_habilitados: modoPedidos === 'whatsapp',
      divulgar_cardapio: modoPedidos === 'link',
      cardapio_cadastrado: usandoOneMenu,
      link_cardapio: linkCardapioResolvido,
      link_vagas: linkVagasResolvido,
      link_feedback: '',
      usar_reservo_reservas: usarReservoReservas,
      usar_feedbackhero_avaliacao_pedidos: usarFeedbackHeroAvaliacaoPedidos,
      usar_cv_vagas: usarCvVagas,
      link_reserva_externo: linkReservaExterno,
      cardapio_link_disponivel: Boolean(linkCardapioResolvido),
      horarios_semana: expandWeeklyScheduleOvernights(form.horarios_semana),
      tabela_frete: normalizeFreteRowsForPayload(form.modelo_frete, form.tabela_frete).sort((left, right) => {
        if (form.modelo_frete !== 'raio') return 0;
        return (parseFreteNumber(readFreteKm(left)) || 0) - (parseFreteNumber(readFreteKm(right)) || 0);
      }),
      endereco_lat: toNumberOrNull(form.endereco_lat),
      endereco_long: toNumberOrNull(form.endereco_long),
      tempo_estimado_entrega_min: toNumberOrNull(form.tempo_estimado_entrega_min),
      tempo_estimado_entrega_max: toNumberOrNull(form.tempo_estimado_entrega_max),
      tempo_estimado_retirada_min: toNumberOrNull(form.tempo_estimado_retirada_min),
      tempo_estimado_retirada_max: toNumberOrNull(form.tempo_estimado_retirada_max),
    };
  };

  const handlePromptPreviewSend = async () => {
    const focusPromptInput = () => {
      const input = promptPreviewInputRef.current;
      if (!input) return;
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    };

    const message = trimText(promptPreviewInput);
    if (!message || promptPreviewLoading) return;

    const nextHistory = [...promptPreviewMessages];
    const userMessage: PromptPreviewChatMessage = { role: 'user', content: message };
    setPromptPreviewMessages((prev) => [...prev, userMessage]);
    setPromptPreviewInput('');
    setPromptPreviewError(null);
    setPromptPreviewLoading(true);
    focusPromptInput();

    try {
      const response = await testPromptConfigChat({
        message,
        history: nextHistory,
        config: hasUnsavedPromptChanges ? buildPayload() : {},
      });
      const assistantText = trimText(response?.response ?? response?.resposta ?? response?.message)
        || 'Sem resposta no momento.';
      setPromptPreviewMessages((prev) => [
        ...prev,
        { role: 'assistant', content: assistantText },
      ]);
    } catch (error) {
      console.error('Erro ao testar conversa:', error);
      setPromptPreviewError(getPromptPreviewErrorMessage(error));
    } finally {
      setPromptPreviewLoading(false);
      focusPromptInput();
    }
  };

  const handlePromptPreviewClear = () => {
    setPromptPreviewMessages([]);
    setPromptPreviewInput('');
    setPromptPreviewError(null);
  };

  const formatFreteCurrency = (index: number) => {
    const value = parseFreteNumber(readFretePreco(form.tabela_frete[index] || {}));
    if (value === null) return;
    updateFreteRow(index, 'preco', value.toFixed(2).replace('.', ','));
  };

  const sortFreteRadiusRules = () => {
    if (form.modelo_frete !== 'raio') return;
    markDirty();
    setForm((prev) => ({
      ...prev,
      tabela_frete: [...prev.tabela_frete].sort((left, right) => {
        const leftKm = parseFreteNumber(readFreteKm(left));
        const rightKm = parseFreteNumber(readFreteKm(right));
        if (leftKm === null) return 1;
        if (rightKm === null) return -1;
        return leftKm - rightKm;
      }),
    }));
  };

  const replicateScheduleToAll = (period: DayPeriod) => {
    markDirty();
    setForm((prev) => ({
      ...prev,
      horarios_semana: prev.horarios_semana.map((day) => ({
        ...day,
        closed: false,
        periods: [{ ...period }],
      })),
    }));
  };

  const flushDraftSave = async () => {
    if (!hasUnsavedPromptChanges) return;
    setDraftSaveState('saving');
    try {
      const response: any = await savePromptDraft(form);
      setDraftSavedAt(response?.draft_updated_at ? new Date(response.draft_updated_at) : new Date());
      setDraftSaveState('saved');
      setSaveError(null);
      notifyAtendabotPromptStatus({ draftPending: true });
    } catch (error) {
      console.error('Erro ao salvar rascunho do atendimento:', error);
      setDraftSaveState('error');
      setSaveError('Não foi possível salvar o rascunho agora.');
    }
  };

  const applyStepValidation = (stepId: AIConfigurationStepId) => {
    const { errors: allErrors } = validate();
    const stepKeys = tabFieldMap[stepId] || [];
    setErrors((prev) => {
      const next = { ...prev };
      stepKeys.forEach((key) => {
        if (allErrors[key]) next[key] = allErrors[key];
        else delete next[key];
      });
      return next;
    });
  };

  const handleSave = async () => {
    const { errors: validationErrors, warnings: validationWarnings } = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setWarnings(validationWarnings);
      setSaveError('Revise os campos obrigatórios.');
      navigateToFirstError(validationErrors);
      return;
    }

    const payload = buildPayload();
    setWarnings(validationWarnings);

    if (saveRequestInFlightRef.current) return;
    saveRequestInFlightRef.current = true;

    setPendingErrorFocus(null);
    setSaving(true);
    setSuccess(false);
    setSaveError(null);
    try {
      const data: any = await savePromptConfig(payload);
      const readyToPublish = data && typeof data.prompt_ready_to_publish === 'boolean'
        ? data.prompt_ready_to_publish
        : true;
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        const published = normalizeForm(data);
        setForm(published);
        setBaselineForm(published);
        setPromptReadyToPublish(readyToPublish);
      }
      setHasUnsavedPromptChanges(false);
      setDraftSaveState('idle');
      setDraftSavedAt(null);
      notifyAtendabotPromptStatus({
        draftPending: false,
        promptReadyToPublish: readyToPublish,
      });
      notifyAtendabotOrderControl(
        data && typeof data.pedidos_habilitados === 'boolean'
          ? data.pedidos_habilitados
          : payload.pedidos_habilitados === true,
      );

      setSuccess(true);
    } catch (error) {
      console.error(error);
      const apiFieldErrors = getApiFieldErrors(error);
      const apiResponseMessage = trimText(error?.response?.data?.error)
        || trimText(error?.response?.data?.message);
      if (Object.keys(apiFieldErrors).length > 0) {
        setErrors(apiFieldErrors);
        setSaveError(apiResponseMessage || 'Revise os campos destacados para publicar o atendimento.');
        navigateToFirstError(apiFieldErrors);
      } else {
        setSaveError(apiResponseMessage || 'Não foi possível publicar o atendimento agora.');
      }
    } finally {
      saveRequestInFlightRef.current = false;
      setSaving(false);
    }
  };

  const goToAdjacentStep = (direction: 1 | -1) => {
    const nextIndex = activeStepIndex + direction;
    if (nextIndex < 0 || nextIndex >= visibleSteps.length) return;
    setActiveTab(visibleSteps[nextIndex].id);
    window.requestAnimationFrame(() => tabPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const handleStepSelect = (stepId: AIConfigurationStepId) => {
    setActiveTab(stepId);
  };

  const handleContinue = () => {
    if (activeTab === 'review') {
      handleSave();
      return;
    }
    applyStepValidation(activeTab);
    setValidatedSteps((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
    flushDraftSave();
    goToAdjacentStep(1);
  };

  useEffect(() => {
    if (loading || !validatedSteps.has(activeTab)) return;
    applyStepValidation(activeTab);
    // Omite `applyStepValidation`: revalidar só quando form/aba/flags mudam; incluir a fn causaria loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, activeTab, loading, validatedSteps, pedidosCardOn, reservasCardOn, reservaLinkOrigem]);

  useEffect(() => {
    if (errors.horarios_semana) setHorariosOpen(true);
  }, [errors.horarios_semana]);

  const reviewValidation = useMemo(
    () => (activeTab === 'review'
      ? validate()
      : { errors: {} as Record<string, string>, warnings: {} as Record<string, string> }),
    // Omite `validate`: função recriada a cada render; entradas relevantes já estão na lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, form, hasReservoProduct, hasFeedbackHeroProduct, hasCvProduct, cvPublicLink, pedidosCardOn, reservasCardOn, reservaLinkOrigem],
  );
  const reviewAlerts = Object.entries(reviewValidation.errors);

  const handleReviewFix = (fieldKey: string) => {
    const targetTab = findTabByFieldKey(fieldKey);
    setErrors(reviewValidation.errors);
    setPendingErrorFocus({ key: fieldKey, tabId: targetTab });
    setActiveTab(targetTab);
  };

  const reviewWarnings = Object.values(reviewValidation.warnings);

  const handleReviewEdit = (stepId: AIConfigurationStepId) => {
    setActiveTab(stepId);
    window.requestAnimationFrame(() => tabPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const reviewSummaries = useMemo(() => {
    if (activeTab !== 'review') return [];
    const errorKeys = new Set(Object.keys(reviewValidation.errors));
    const freteLabels: Record<string, string> = {
      bairros_cep: 'taxa por bairro',
      raio: 'taxa por raio (km)',
      fixa: 'taxa fixa',
    };

    const booleanText = (value: boolean | null) =>
      value === true ? 'Sim' : value === false ? 'Não' : 'Não definido';

    const enderecoResumo = [form.rua, form.numero, form.bairro, form.cidade, form.estado]
      .map((item) => trimText(item))
      .filter(Boolean)
      .join(', ') || 'Não informado';

    const contatoResumo = [
      trimText(form.telefone_ligacoes) && `telefone ${trimText(form.telefone_ligacoes)}`,
      trimText(form.email_atendimento) && `e-mail ${trimText(form.email_atendimento)}`,
      trimText(form.site) && `site ${trimText(form.site)}`,
    ].filter(Boolean).join(' · ') || 'Nenhum canal informado';

    const cardapioResumo = form.usar_cardapio_onemenu
      ? 'OneMenu (conectado)'
      : trimText(form.link_cardapio)
        ? `Link externo: ${trimText(form.link_cardapio)}`
        : form.cardapio_cadastrado === true
          ? 'Cardápio informado à IA'
          : 'Não configurado';

    const entregaResumo = form.entrega_ativa === true
      ? [
          `Sim. Prazo de ${form.tempo_estimado_entrega_min || 'não informado'} a ${form.tempo_estimado_entrega_max || 'não informado'} min`,
          form.modelo_frete ? freteLabels[form.modelo_frete] || form.modelo_frete : '',
          form.tabela_frete.length > 0
            ? `${form.tabela_frete.length} ${form.tabela_frete.length === 1 ? 'regra' : 'regras'} de taxa`
            : '',
        ].filter(Boolean).join('. ')
      : booleanText(form.entrega_ativa);

    const retiradaResumo = form.retirada_ativa === true
      ? `Sim. Prazo de ${form.tempo_estimado_retirada_min || 'não informado'} a ${form.tempo_estimado_retirada_max || 'não informado'} min`
      : booleanText(form.retirada_ativa);

    const pagamentoResumo = form.metodos_pagamento.length > 0
      ? [
          form.metodos_pagamento.join(', '),
          form.metodos_pagamento.includes('pix')
            ? (trimText(form.pix_chave) ? 'chave Pix cadastrada' : 'chave Pix pendente')
            : '',
        ].filter(Boolean).join('. ')
      : 'Não informado';

    const reservasResumo = !reservasCardOn
      ? 'Não ativadas. A IA responde que o restaurante não trabalha com reservas.'
      : modoReservas === 'whatsapp'
        ? 'A IA registra a reserva na conversa (Reservo)'
        : modoReservas === 'link'
          ? reservaOrigemEfetiva === 'reservo'
            ? `A IA envia o link do Reservo${reservoPublicLink ? `: ${reservoPublicLink}` : ' (gerado automaticamente)'}`
            : trimText(form.link_reserva_externo)
              ? `A IA envia o link externo: ${trimText(form.link_reserva_externo)}`
              : 'A IA envia um link, mas ele ainda está pendente.'
          : 'Modo de reservas pendente';

    const eventosResumo = [
      hasType('eventos') && 'Eventos',
      hasType('encomendas') && 'Encomendas',
    ].filter(Boolean).join(', ') || 'Não';

    const itemsByStep: Partial<Record<AIConfigurationStepId, ReviewSummaryItem[]>> = {
      capabilities: [
        {
          label: 'A IA vai',
          value: activeCapabilityTitles.join(' · '),
        },
      ],
      restaurant: [
        { label: 'Nome', value: trimText(form.nome_empresa) || 'Não informado' },
        { label: 'Endereço', value: enderecoResumo },
        {
          label: 'Horários',
          value: openingHoursSummary,
        },
        { label: 'Contato', value: contatoResumo },
        { label: 'Pagamento', value: pagamentoResumo },
        { label: 'Eventos e encomendas', value: eventosResumo },
      ],
      orders: [
        {
          label: 'Modo',
          value: modoPedidos === 'whatsapp'
            ? 'A IA registra pedidos na conversa do WhatsApp'
            : modoPedidos === 'link'
              ? 'A IA envia o link do cardápio (não registra pedidos)'
              : 'Pendente: escolha o modo de pedidos.',
        },
        { label: 'Cardápio', value: cardapioResumo },
      ],
      fulfillment: [
        { label: 'Entrega', value: entregaResumo },
        { label: 'Retirada', value: retiradaResumo },
      ],
      additional: [
        { label: 'Reservas', value: reservasResumo },
        {
          label: 'Avaliações após o pedido',
          value: form.usar_feedbackhero_avaliacao_pedidos === true ? 'Sim (FeedbackHero)' : 'Não',
        },
        { label: 'Vagas', value: usaCvVagas ? 'Sim (Currículo Certo)' : 'Não' },
      ],
      personality: [
        { label: 'Nome da atendente', value: trimText(form.nome_do_agente) || 'Ana (padrão)' },
        { label: 'Tom de voz', value: trimText(form.personalidade_principal) || 'Não definido' },
        { label: 'Emojis', value: trimText(form.uso_de_emojis) || 'Não definido' },
        { label: 'Exemplo de saudação', value: <em>{personalityPreview}</em> },
      ],
    };

    return visibleSteps
      .filter((step) => step.id !== 'review')
      .map((step) => {
        const hasIssue = tabFieldMap[step.id].some((key) => errorKeys.has(key));
        const status: ReviewSummaryStatus = hasIssue ? 'attention' : 'ok';
        return {
          id: step.id,
          title: step.title,
          icon: step.icon,
          status,
          items: itemsByStep[step.id] || [],
        };
      });
    // Omite helpers/derivados recriados a cada render; deps listam as entradas que mudam o resumo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviewValidation, form, openingHoursSummary, usaReservoReservas, usaCvVagas, personalityPreview, tabFieldMap, pedidosCardOn, reservasCardOn, reservaLinkOrigem]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" role="status" aria-label="Carregando configuração do atendimento">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center bg-[#f6f7fb] p-4">
          <div className="w-full max-w-lg rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm" role="alert">
            <AlertCircle className="mx-auto text-rose-600" size={30} />
            <h1 className="mt-3 text-lg font-bold text-slate-900">Não foi possível abrir a configuração</h1>
            <p className="mt-2 text-sm text-slate-600">{loadError}</p>
            <button type="button" className="ai-button is-primary mt-5" onClick={() => setLoadRequestKey((value) => value + 1)}>
              Tentar novamente
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {saving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/55 backdrop-blur-sm" role="status" aria-live="polite">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            <span className="text-slate-700 text-sm font-semibold">Publicando atendimento…</span>
          </div>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8"
      >
        <ConfigurationHeader
          saveState={draftSaveState}
          savedAt={draftSavedAt}
          onTest={() => setConversationDrawerOpen(true)}
        />
        <div className="mx-auto w-full max-w-[1400px] space-y-4">
          {!loading && !promptReadyToPublish && (
            <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800" role="alert">
              <AlertCircle size={17} className="flex-shrink-0" />
              Atendimento não publicado. Preencha as informações obrigatórias e clique em “Publicar atendimento”. O Atendabot só poderá ser ativado depois disso.
            </div>
          )}
          {atendabotTrialPromptOnly && (
            <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-teal-900">Durante o período de teste, você pode configurar e simular o atendimento.</p>
                  <p className="mt-1 text-xs text-teal-800/80">Ative o AtendaBot para liberar conversas, pedidos, integrações e a operação completa.</p>
                </div>
                <button type="button" onClick={() => openProductCheckout('atendabot')} className="ai-button is-primary">
                  <CreditCard size={15} /> Ativar AtendaBot
                </button>
              </div>
            </div>
          )}
          {hasUnsavedPromptChanges && (
            <div className="py-2">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-2 rounded-2xl border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 px-3 py-2.5 shadow-[0_8px_24px_rgba(180,83,9,0.10)]" role="alert">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-800" aria-hidden="true">
                  <AlertCircle size={20} />
                </span>
                <p className="text-center text-sm font-extrabold text-amber-950">Você tem alterações não publicadas.</p>
                <button
                  type="button"
                  onClick={() => {
                    handleStepSelect('review');
                    window.requestAnimationFrame(() => tabPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                  }}
                  className="col-span-3 inline-flex min-h-9 items-center justify-center justify-self-center rounded-lg bg-amber-700 px-4 text-sm font-bold text-white transition hover:bg-amber-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700 sm:col-span-1 sm:justify-self-end"
                >
                  Revisar e publicar
                </button>
              </div>
            </div>
          )}
          {saveError && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" role="alert">
              <span className="flex items-center gap-2"><X size={16} /> {saveError}</span>
              {draftSaveState === 'error' && <button type="button" className="ai-button is-secondary" onClick={flushDraftSave}>Tentar novamente</button>}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" role="status">
              <CheckCircle2 size={17} /> Atendimento atualizado com sucesso.
            </div>
          )}
          <DraftFieldsContext.Provider value={draftFieldKeys}>
          <AIConfigurationLayout steps={navigationSteps} activeStep={activeTab} onSelectStep={handleStepSelect}>
            <div ref={tabPanelRef} className="form-prompt-content" role="tabpanel" id={`tab-panel-${activeTab}`}>
              <div className={`ai-current-step-heading${activeTab === 'capabilities' ? ' is-compact' : ''}`}>
                {activeTab !== 'capabilities' && (
                  <span className="ai-current-step-icon" aria-hidden="true">
                    <ActiveStepIcon size={22} />
                  </span>
                )}
                <div>
                  {activeTab !== 'capabilities' && (
                    <span className="ai-current-step-eyebrow">Etapa {activeStepIndex + 1} de {visibleSteps.length}</span>
                  )}
                  <h2>{activeStepDefinition.title}</h2>
                  {activeTab !== 'capabilities' && <p>{activeStepDefinition.description}</p>}
                </div>
                {activeTab !== 'capabilities' && activeTab !== 'review' && activeTab !== 'personality' && (
                  <small>Campos marcados com * são obrigatórios.</small>
                )}
              </div>
              {activeTab === 'restaurant' && (isOwner || isAdmin) && showIfoodImportInvite && (
                <div className="mb-5 flex flex-col items-start gap-2 rounded-2xl border border-rose-100 bg-gradient-to-r from-rose-50 to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Já possui uma loja no iFood?</p>
                    <p className="mt-0.5 text-xs text-slate-600">Copie o endereço e os horários para evitar preencher tudo novamente.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenIfoodImport}
                    disabled={ifoodImportLoading}
                    className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#EA1D2C] px-4 text-sm font-bold text-white transition hover:bg-[#c91524] disabled:cursor-wait disabled:bg-slate-300"
                  >
                    {ifoodImportLoading ? 'Verificando conexão…' : 'Buscar informações no iFood'}
                  </button>
                </div>
              )}
              {activeTab === 'capabilities' && (
                <CapabilitiesStep
                  isAtendabotBrand={isAtendabotBrand}
                  pedidosFeatureOn={pedidosFeatureOn}
                  reservasFeatureOn={reservasFeatureOn}
                  modoPedidos={modoPedidos}
                  modoReservas={modoReservas}
                  pedidosModeOptions={PEDIDOS_MODE_OPTIONS}
                  reservasModeOptions={reservasModeOptions}
                  visibleCapabilityCards={visibleCapabilityCards}
                  fulfillmentForcado={fulfillmentForcado}
                  fulfillmentCardOn={fulfillmentCardOn}
                  showFeedbackPedidos={showFeedbackPedidos}
                  vagasOn={vagasOn}
                  fulfillmentDirty={fulfillmentDirty}
                  reviewsDirty={reviewsDirty}
                  jobsDirty={jobsDirty}
                  modoPedidosDirty={modoPedidosDirty}
                  modoReservasDirty={modoReservasDirty}
                  errors={errors}
                  visibleSteps={visibleSteps}
                  pedidoChecklist={pedidoChecklist}
                  onTogglePedidos={togglePedidosCard}
                  onToggleReservas={toggleReservasCard}
                  onSelectModoPedidos={(id) => setModoPedidos(id as ModoPedidos)}
                  onSelectModoReservas={(id) => setModoReservas(id as ModoReservas)}
                  onToggleCapability={toggleCapability}
                />
              )}

            {activeTab === 'restaurant' && (
              <RestaurantStep
                form={form}
                errors={errors}
                draftFieldKeys={draftFieldKeys}
                updateField={updateField}
                showEnderecoTab={showEnderecoTab}
                showEndereco={showEndereco}
                showCidadeEstado={showCidadeEstado}
                showPresencial={showPresencial}
                showHorarios={showHorarios}
                showLinks={showLinks}
                enderecoObrigatorio={enderecoObrigatorio}
                modoPedidos={modoPedidos}
                enderecoBusca={enderecoBusca}
                buscandoEndereco={buscandoEndereco}
                enderecoResultados={enderecoResultados}
                setEnderecoBusca={setEnderecoBusca}
                setEnderecoResultados={setEnderecoResultados}
                handleBuscarEndereco={handleBuscarEndereco}
                handleSelecionarEndereco={handleSelecionarEndereco}
                mapPreviewUrl={mapPreviewUrl}
                UF_OPTIONS={UF_OPTIONS}
                CITY_OPTIONS_BY_UF={CITY_OPTIONS_BY_UF}
                horariosOpen={horariosOpen}
                setHorariosOpen={setHorariosOpen}
                openingHoursSummary={openingHoursSummary}
                toggleDayClosed={toggleDayClosed}
                addPeriod={addPeriod}
                updatePeriod={updatePeriod}
                removePeriod={removePeriod}
                replicateScheduleToAll={replicateScheduleToAll}
                addSpecialDate={addSpecialDate}
                removeSpecialDate={removeSpecialDate}
                updateSpecialDate={updateSpecialDate}
                addSpecialPeriod={addSpecialPeriod}
                removeSpecialPeriod={removeSpecialPeriod}
                updateSpecialPeriod={updateSpecialPeriod}
                PAYMENT_OPTIONS={PAYMENT_OPTIONS}
                toggleArrayValue={toggleArrayValue}
                bandeiraInput={bandeiraInput}
                setBandeiraInput={setBandeiraInput}
                addBandeira={addBandeira}
                removeBandeira={removeBandeira}
                showLinksAdvanced={showLinksAdvanced}
                setShowLinksAdvanced={setShowLinksAdvanced}
                eventosOn={eventosOn}
                encomendasOn={encomendasOn}
                eventosDirty={eventosDirty}
                encomendasDirty={encomendasDirty}
                setAtendimentoType={setAtendimentoType}
              />
            )}

            {activeTab === 'personality' && (
              <PersonalityStep
                form={form}
                errors={errors}
                personalityPreview={personalityPreview}
                updateField={updateField}
              />
            )}



            {activeTab === 'orders' && (
              <OrdersStep
                isAtendabotBrand={isAtendabotBrand}
                showCardapio={showCardapio}
                showRegras={showRegras}
                form={form}
                errors={errors}
                modoPedidos={modoPedidos}
                modoPedidosDirty={modoPedidosDirty}
                pedidosModeOptions={PEDIDOS_MODE_OPTIONS}
                setModoPedidos={setModoPedidos}
                cardapioLinkPreview={cardapioLinkPreview}
                usaCardapioOneMenu={usaCardapioOneMenu}
                externalCardapioInputValue={externalCardapioInputValue}
                oneMenuAutoLink={oneMenuAutoLink}
                openRelatedProduct={openRelatedProduct}
                updateField={updateField}
              />
            )}
            {!isAtendabotBrand && activeTab === 'additional' && (
              <AdditionalStep
                form={form}
                errors={errors}
                reservasFeatureOn={reservasFeatureOn}
                showFeedbackPedidos={showFeedbackPedidos}
                showVagas={showLinks && hasType('vagas_fornecedores_feedback')}
                modoReservas={modoReservas}
                modoReservasDirty={modoReservasDirty}
                reservasModeOptions={reservasModeOptions}
                setModoReservas={setModoReservas}
                hasReservoProduct={hasReservoProduct}
                reservaOrigemOptions={reservaOrigemOptions}
                reservaOrigemEfetiva={reservaOrigemEfetiva}
                setReservaLinkOrigem={setReservaLinkOrigem}
                markDirty={markDirty}
                reservoPublicLink={reservoPublicLink}
                reservaExternoLink={reservaExternoLink}
                reservaLinkPreview={reservaLinkPreview}
                hasReservoWhatsappContractLink={hasReservoWhatsappContractLink}
                reservoWhatsappContractUrl={RESERVO_WHATSAPP_CONTRACT_URL}
                hasFeedbackHeroProduct={hasFeedbackHeroProduct}
                hasFeedbackHeroWhatsappContractLink={hasFeedbackHeroWhatsappContractLink}
                feedbackHeroWhatsappContractUrl={FEEDBACKHERO_WHATSAPP_CONTRACT_URL}
                hasCvProduct={hasCvProduct}
                usaCvVagas={usaCvVagas}
                cvLinkExample={cvLinkExample}
                cvPublicLink={cvPublicLink}
                openRelatedProduct={openRelatedProduct}
                openProductCheckout={openProductCheckout}
                openExternalLink={openExternalLink}
                updateField={updateField}
              />
            )}
            {activeTab === 'fulfillment' && (
              <FulfillmentStep
                fulfillmentForcado={fulfillmentForcado}
                form={form}
                errors={errors}
                entregaObrigatoria={entregaObrigatoria}
                retiradaObrigatoria={retiradaObrigatoria}
                showEntrega={showEntrega}
                showRetirada={showRetirada}
                modoPedidos={modoPedidos}
                freteMeta={freteMeta}
                freteHasAreaField={freteHasAreaField}
                freteHasKmField={freteHasKmField}
                freteHasTempoField={freteHasTempoField}
                freteRowGridClass={freteRowGridClass}
                deliveryPreviewText={deliveryPreviewText}
                pedidoChecklist={pedidoChecklist}
                updateField={updateField}
                updateFulfillmentAvailability={updateFulfillmentAvailability}
                updateModeloFrete={updateModeloFrete}
                updateFreteRow={updateFreteRow}
                addFreteRow={addFreteRow}
                removeFreteRow={removeFreteRow}
                formatFreteCurrency={formatFreteCurrency}
                sortFreteRadiusRules={sortFreteRadiusRules}
                readFreteArea={readFreteArea}
                readFreteKm={readFreteKm}
                readFreteTempo={readFreteTempo}
                readFretePreco={readFretePreco}
                parseFreteNumber={parseFreteNumber}
                handleReviewEdit={handleReviewEdit}
              />
            )}






            {activeTab === 'review' && (
              <ReviewStep
                activeCapabilityTitles={activeCapabilityTitles}
                reviewAlerts={reviewAlerts}
                reviewWarnings={reviewWarnings}
                hasUnsavedPromptChanges={hasUnsavedPromptChanges}
                reviewSummaries={reviewSummaries}
                onOpenConversationTest={() => setConversationDrawerOpen(true)}
                onReviewFix={handleReviewFix}
                onReviewEdit={handleReviewEdit}
              />
            )}
            </div>
            <BottomNavigation
              canGoBack={activeStepIndex > 0}
              isLastStep={activeTab === 'review'}
              busy={saving}
              onBack={() => goToAdjacentStep(-1)}
              onContinue={handleContinue}
            />
          </AIConfigurationLayout>
          </DraftFieldsContext.Provider>
        </div>
      </motion.div>
      <ConversationTestDrawer open={conversationDrawerOpen} onClose={() => setConversationDrawerOpen(false)}>
        <div className="form-prompt-whatsapp-stage">
          <div className="form-prompt-whatsapp-shell" aria-label="Conversa de teste com o AtendaBot">
            <div className="form-prompt-whatsapp-header">
              <div className="form-prompt-whatsapp-avatar"><Bot size={18} /></div>
              <div className="min-w-0">
                <p className="form-prompt-whatsapp-name">AtendaBot</p>
                <span className="form-prompt-whatsapp-status">{promptPreviewLoading ? 'digitando…' : 'online'}</span>
              </div>
            </div>
            <div ref={promptPreviewMessagesRef} className="form-prompt-whatsapp-messages" aria-live="polite">
              <div className="form-prompt-whatsapp-day">Hoje</div>
              {promptPreviewMessages.length === 0 && (
                <div className="form-prompt-whatsapp-empty">Envie uma mensagem para validar o atendimento configurado.</div>
              )}
              {promptPreviewMessages.map((item, index) => {
                const isAssistant = item.role === 'assistant';
                return (
                  <div key={`${item.role}-${index}`} className={`form-prompt-whatsapp-row${isAssistant ? ' is-assistant' : ' is-user'}`}>
                    <div className="form-prompt-whatsapp-bubble">
                      <span
                        className="form-prompt-whatsapp-text"
                        {...(isAssistant ? { dangerouslySetInnerHTML: { __html: formatMarkdownToSafeHtml(trimText(item.content)) } } : {})}
                      >
                        {!isAssistant ? item.content : null}
                      </span>
                      <span className="form-prompt-whatsapp-time">agora</span>
                    </div>
                  </div>
                );
              })}
              {promptPreviewLoading && (
                <div className="form-prompt-whatsapp-row is-assistant">
                  <div className="form-prompt-whatsapp-bubble is-typing" aria-label="AtendaBot digitando"><span /><span /><span /></div>
                </div>
              )}
            </div>
            <div className="form-prompt-whatsapp-composer">
              <textarea
                ref={promptPreviewInputRef}
                className="form-prompt-whatsapp-input"
                value={promptPreviewInput}
                placeholder="Mensagem"
                aria-label="Mensagem para o AtendaBot"
                onChange={(event) => setPromptPreviewInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handlePromptPreviewSend();
                  }
                }}
                rows={1}
              />
              <button type="button" onClick={handlePromptPreviewClear} disabled={promptPreviewLoading || promptPreviewMessages.length === 0} className="form-prompt-whatsapp-icon-button" aria-label="Limpar conversa">
                <Trash2 size={17} />
              </button>
              <button type="button" onClick={handlePromptPreviewSend} disabled={promptPreviewLoading || !trimText(promptPreviewInput)} className="form-prompt-whatsapp-send" aria-label="Enviar mensagem">
                <Send size={18} />
              </button>
            </div>
            {promptPreviewError && <p className="form-prompt-whatsapp-error" role="alert">{promptPreviewError}</p>}
          </div>
        </div>
      </ConversationTestDrawer>
      <IfoodPromptImportDialog
        open={ifoodImportOpen}
        merchantId={ifoodMerchant?.id || ''}
        merchantName={ifoodMerchant?.name}
        onClose={() => setIfoodImportOpen(false)}
        onApply={handleApplyIfoodImport}
        onReconnect={() => {
          setIfoodImportOpen(false);
          navigate('/configuracoes/ifood');
        }}
      />
      <ProductCheckoutModal
        open={Boolean(activeCheckoutProductKey)}
        // @ts-ignore ProductCheckoutModal is untyped .jsx; TS misinfers this prop as null-only
        initialProductKey={activeCheckoutProductKey}
        allowedProductKeys={isAtendabotBrand ? ['atendabot'] : null}
        title={isAtendabotBrand ? 'Ativar AtendaBot' : 'Ativar produtos'}
        checkoutBrandName={isAtendabotBrand ? 'AtendaBot' : undefined}
        checkoutDescription={isAtendabotBrand
          ? 'Confira os dados para ativar seu atendimento com IA.'
          : undefined}
        checkoutSummaryDescription={isAtendabotBrand
          ? 'Confira o valor mensal antes de concluir a ativação.'
          : undefined}
        productDescriptionOverrides={isAtendabotBrand
          ? { atendabot: 'Atendimento com IA no WhatsApp.' }
          : undefined}
        onClose={() => setCheckoutProductKey(null)}
      />
    </Layout>
  );
};

export default FormPrompt;
